import React, { useEffect, useMemo, useState, useRef, useCallback, KeyboardEvent, ChangeEvent } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom"; // Import useParams from React Router
import { format } from "date-fns";
import { messageHandler } from "utils/messageHandler";
import { TEAMMEMBER_MESSAGES, AGENT_MESSAGES, ADDRESSADDEDIT_MESSAGES, AGENTOFFICEPRODUCTADDEDIT_MESSAGES, BROKERAGE_FIRM_MESSAGES } from "utils/messages";
import { Row } from "@tanstack/react-table";
import { Agent, Office, Addresses } from 'types';
import { Product, LinkedInstaller, Address } from "types";
import DataTable from "components/DataTable";
import PaginationControls from "components/PaginationControls";
import { StatusBadge } from "components/Productlocationstatus";
import { MobileCardsOnly } from "components/TableandMobilecomponent";
import { DesktopTableLoader, MobileCardsLoader } from "components/DataTableLoader";
import PaginationControlsView from "components/PaginationViewtables";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";
import EntityProfileViewCard from "components/EntityProfileViewCard";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import { UIInput, UIInputDisable, UIReactSelect, UIReactMultiSelect } from "components/Inputfields";
import { useFormik } from "formik";
import * as Yup from "yup";
import AddressAutocomplete from "components/AddressAutocomplete";
import {
  addressLine1Validation,
  cityValidation,
  stateValidation,
  zipcodeValidation,
} from "validationSchemas/common";

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

import {
  ExportAsExcel,
} from "@siamf/react-export";

import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { stringify } from "querystring";
import { ag } from "assets/libs/chart.js/chunks/helpers.segment";
import { getAuthCookie } from "../../utils/authCookies";
import { postUserLicenseToggle } from "../../utils/userLicenseToggle";

const US_COUNTRY_DEFAULTS = { country: "United States", countryCode: "US" } as const;
const DEFAULT_ADDRESS_PHONE = "0000000000";

const resolveAddressPhone = (phone: unknown) =>
  (phone ?? "").toString().trim() || DEFAULT_ADDRESS_PHONE;

interface FirmInfo {
  companyID: string;
  companyName: string;
  address: string;
}

interface BrokerageFirm {
  id: string;
  companyName: string;
  firstName: string;
  lastName: string;
  phone1: string;
  phone2: string;
  email: string;
  status: string;
  profilePic?: string;
}

type Teammember = {
  _id: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
  phone: string;
  email: string;
  offices?: Office[];
  officeNames?: string;
  permissions: string;
  password: string;
  image?: string;
  status: string;
  licenseType: string;
  /** User license on/off (datatable). Prefer over `status` for Activate/Deactivate UI when present. */
  LicenseActive?: boolean;
  /** When true, user is already a brokerage admin — show label instead of promote action. */
  is_promoted_as_brokerage_admin?: boolean;
};

/** License toggle UI: API may send LicenseActive false while status is still "Active". */
function isTeamMemberLicenseActive(tm: Teammember): boolean {
  if (typeof tm.LicenseActive === "boolean") {
    return tm.LicenseActive;
  }
  return String(tm.status || "").trim().toLowerCase() === "active";
}

type OfficeInventoryObj = {
  product_id: string;
  office: Office;
  brokerage_firm: String;
  product: Product;
  created_by: {
    id: string;
    name: string;
  };
};

type AgentProduct = {
  _id: string;
  product_id?: string;
  agent_id: string;
  agent_email: string;
  agent_name: string;
  product: Product;
  created_by: { id: string; name: string };
};

type DeleteTarget =
  | { type: "teammember"; id: string; name: string }
  | { type: "office"; id: string; name: string }
  | { type: "officeInventory"; id: string; secondaryId: string; name: string }
  | { type: "agent"; id: string; name: string }
  | { type: "agentInventory"; id: string; secondaryId: string; name: string }
  | { type: "address"; id: string; name: string };

interface ExportRow {
  "First Name": string;
  "Last Name": string;
  "Job Role": string;
  "Phone number": string;
  "Email": string;
  "Assgined offices": string;
  "Status": string;
}

function fuzzyTextFilter<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
) {
  const raw = row.getValue(columnId);
  const value = (typeof raw === "boolean"
    ? raw
      ? "Active"
      : "Inactive"
    : String(raw)
  ).toLowerCase();

  return value.includes(filterValue.toLowerCase());
}

const teammemberColumnHelper = createColumnHelper<Teammember>();
const officeColumnHelper = createColumnHelper<Office>();
const officeInventoryColumnHelper = createColumnHelper<OfficeInventoryObj>();
const agentColumnHelper = createColumnHelper<Agent>();
const agentInventoryColumnHelper = createColumnHelper<AgentProduct>();
const addressColumnHelper = createColumnHelper<Addresses>();

const HEX24 = /^[a-f0-9]{24}$/i;
const ABS_URL = /^https?:\/\//i;
const isHexId = (s?: string) => !!(s && HEX24.test(s.trim()));

const SignageBrokeragePage = () => {
  const { firmId } = useParams<{ firmId: string }>();
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId") || "";
  const [brokerageFirm, setBrokerageFirm] = useState<BrokerageFirm | null>(null) // To store the brokerageFirm data
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [teammemberLoading, setTeammemberLoading] = useState(true);
  const [officeLoading, setOfficeLoading] = useState(true);
  const [officeInventoryLoading, setOfficeInventoryLoading] = useState(true);
  const [agentLoading, setAgentLoading] = useState(true);
  const [agentInventoryLoading, setAgentInventoryLoading] = useState(true);
  const [addressLoading, setAddressLoading] = useState(true);
  const [teammembers, setTeammembers] = useState<Teammember[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [officeProducts, setOfficeProducts] = useState<OfficeInventoryObj[]>(
    []
  );
  const [agentProducts, setAgentProducts] = useState<AgentProduct[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Determine the base route based on current location
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';
  const [teammemberGlobalFilter, setTeammemberGlobalFilter] = useState("");
  const [officeGlobalFilter, setOfficeGlobalFilter] = useState("");
  const [officeInventoryGlobalFilter, setOfficeInventoryGlobalFilter] = useState("");
  const [agentGlobalFilter, setAgentGlobalFilter] = useState("");
  const [agentInventoryGlobalFilter, setAgentInventoryGlobalFilter] = useState("");
  const [addressGlobalFilter, setAddressGlobalFilter] = useState("");

  const [statusFilter, setStatusFilter] = useState("");

  const [teammemberRowCount, setTeammemberRowCount] = useState(0);
  const [officeRowCount, setOfficeRowCount] = useState(0);
  const [officeInventoryRowCount, setOfficeInventoryRowCount] = useState(0);
  const [agentRowCount, setAgentRowCount] = useState(0);
  const [agentInventoryRowCount, setAgentInventoryRowCount] = useState(0);
  const [addressRowCount, setAddressRowCount] = useState(0);

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [exportRows, setExportRows] = useState<ExportRow[]>([]);

  const [teammembersSearchInput, setTeammembersSearchInput] = useState<string>(teammemberGlobalFilter ?? "");
  const [officeSearchInput, setOfficeSearchInput] = useState<string>(officeGlobalFilter ?? "");
  const [officeInventorySearchInput, setOfficeInventorySearchInput] = useState<string>(officeInventoryGlobalFilter ?? "");
  const [agentSearchInput, setAgentSearchInput] = useState<string>(agentGlobalFilter ?? "");
  const [agentInventorySearchInput, setAgentInventorySearchInput] = useState<string>(agentInventoryGlobalFilter ?? "");
  const [addressSearchInput, setAddressSearchInput] = useState<string>(addressGlobalFilter ?? "");

  const [activeTab, setActiveTab] = useState<string>(window.location.hash || "#teammembers");

  const [teammemberPagination, setTeammemberPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [teammemberRefreshTrigger, setTeammemberRefreshTrigger] = useState(0);
  const [promoteLoadingId, setPromoteLoadingId] = useState<string | null>(null);
  const [teamMemberLicenseToggleId, setTeamMemberLicenseToggleId] = useState<string | null>(null);
  const [officePagination, setOfficePagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [officeInventoryPagination, setOfficeInventoryPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [agentPagination, setAgentPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [agentInventoryPagination, setAgentInventoryPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [addressPagination, setAddressPagination] = useState({ pageIndex: 0, pageSize: 10 });

  // Address state
  const [addresses, setAddresses] = useState<Addresses[]>([]);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Addresses | null>(null);


  const fetchBrokerageFirmData = useCallback(async () => {
    if (!firmId || !token) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/users/${firmId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) {
        throw new Error(BROKERAGE_FIRM_MESSAGES.fetchSingle.error);
      }
      const data = await response.json();
      if (data && data.firmDetails && data.firmDetails.address) {
        setBrokerageFirm({
          id: data.id || "",
          companyName: data.firmDetails.companyName || "",
          firstName: data.firstName || "",
          lastName: data.lastName || "",
          email: data.email || "",
          phone1: data.phone1 || "",
          phone2: data.phone2 || "",
          status: data.status || "Active",
          profilePic: data.profilePic || "",
        });
      } else {
        console.error("Incomplete data", data);
      }
    } catch (error) {
      messageHandler.brokerageFirm.error("fetchSingle", error);
      setError(BROKERAGE_FIRM_MESSAGES.fetchSingle.error);
      console.error("Error fetching brokerageFirm data:", error);
    }
  }, [firmId, token]);

  useEffect(() => {
    void fetchBrokerageFirmData();
  }, [fetchBrokerageFirmData]);

  useEffect(() => {
    const fetchTeammembers = async () => {
      setTeammemberLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users-search/datatable`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              page: teammemberPagination.pageIndex + 1,
              limit: teammemberPagination.pageSize,
              search: teammemberGlobalFilter,
              filters: {
                // userType: "team_member",
                // status: "Active",
                // LicenseActive: "true",
                // linked_suppliers: "TEAM_MEMBER",
                licenseType: "BROKERAGE_TEAM_MEMBER",
                brokerageFirmID: firmId,
                // "created_by.id": firmId,
              },
              sort: {
                field: "created_at",
                order: "desc",
              },
              // fields: ["id", "firmDetails", "agentDetails", "offices", "firstName", "lastName", "email", "LicenseActive", "userType", "status", "created_at"],
            }),
          }
        );
        if (!response.ok) {
          throw new Error(TEAMMEMBER_MESSAGES.fetch.error);
        }
        const result = await response.json();
        setTeammembers(result?.data || []);
        setTeammemberRowCount(result?.total || 0);
      } catch (err) {
        messageHandler.error("teammember", "fetch", err);
        setError(TEAMMEMBER_MESSAGES.fetch.error);
      } finally {
        setTeammemberLoading(false);
      }
    };

    fetchTeammembers();
  }, [token, teammemberGlobalFilter, teammemberPagination, teammemberRefreshTrigger]);

  // useEffect(() => {
  //   const fetchOffices = async () => {
  //     try {
  //       const response = await fetch(
  //         `${process.env.REACT_APP_API_URL}/offices-search?created_by.id=${firmId}`,
  //         {
  //           method: "GET",
  //           headers: {
  //             Authorization: `Bearer ${token}`,
  //             "Content-Type": "application/json",
  //           },
  //         }
  //       );
  //       const data = await response.json();
  //       setOffices(data);
  //     } catch (err) {
  //       setError(
  //         err instanceof Error ? err.message : "An unknown error occurred"
  //       );
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchOffices();
  // }, [token, officeGlobalFilter, pagination]);

  useEffect(() => {
    const fetchOffices = async () => {
      setOfficeLoading(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/offices/datatable`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            page: officePagination.pageIndex + 1,
            limit: officePagination.pageSize,
            search: officeGlobalFilter,
            filters: {
              "created_by.id": firmId,
            },
            sort: {
              "field": "created_at",
              "order": "desc"
            },
            // fields: ["id", "label", "officeName", "officeType", "officeAddress", "status", "created_at"]

          }),
        });
        if (!response.ok) {
          throw new Error(AGENT_MESSAGES.fetchOffices.error);
        }
        const result = await response.json();
        setOffices(result?.data || []);
        setOfficeRowCount(result?.total || 0);
      } catch (err) {
        messageHandler.agent.error("fetch", err);
        setError(AGENT_MESSAGES.fetchOffices.error);
      } finally {
        setOfficeLoading(false);
      }
    };

    fetchOffices();
  }, [token, officeGlobalFilter, officePagination]);

  useEffect(() => {
    const fetchAgents = async () => {
      setAgentLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users-search/datatable`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              page: agentPagination.pageIndex + 1,
              limit: agentPagination.pageSize,
              search: agentGlobalFilter,
              filters: {
                // userType: "agent",
                // status: "Active",
                // LicenseActive: "true",
                // linked_suppliers: "SIGNAGE_AGENT",
                // "created_by.id": firmId,
                licenseType: "BROKERAGE_AGENT",
                brokerageFirmID: firmId,
              },
              sort: {
                field: "created_at",
                order: "desc",
              },
              fields: ["id", "firmDetails", "agentDetails", "offices", "firstName", "lastName", "email", "LicenseActive", "userType", "status", "created_at"],
            }),
          }
        );

        if (!response.ok) {
          throw new Error(AGENT_MESSAGES.fetch.error);
        }

        const result = await response.json();
        console.log("Agent Data:", result);
        setAgents(result?.data ?? []);
        setAgentRowCount(result?.total || 0);
      } catch (err) {
        messageHandler.agent.error("fetch", err);
        setError(AGENT_MESSAGES.fetch.error);
      } finally {
        setAgentLoading(false);
      }
    };

    fetchAgents();
  }, [token, agentGlobalFilter, agentPagination, deleting, firmId]);

  useEffect(() => {
    const fetchOfficeProducts = async () => {
      setOfficeInventoryLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/brokerage-offices/${firmId}/inventory/datatable`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              page: officeInventoryPagination.pageIndex + 1,
              limit: officeInventoryPagination.pageSize,
              search: officeInventoryGlobalFilter,
              filters: {
                "created_by.id": firmId,
              },
              sort: {
                field: "created_at",
                order: "desc",
              },
            }),
          }
        );

        if (!response.ok) {
          throw new Error(AGENTOFFICEPRODUCTADDEDIT_MESSAGES.fetch.error);
        }

        const result = await response.json();
        // Ensure the response contains the expected structure
        if (result && Array.isArray(result?.data)) {
          setOfficeProducts(result?.data || []);
          setOfficeInventoryRowCount(result?.total || 0);
        } else {
          console.error("Unexpected API response structure:", result);
        }
      } catch (err) {
        messageHandler.product.error("fetch", err);
        setError(AGENTOFFICEPRODUCTADDEDIT_MESSAGES.fetch.error);
        console.error("Error fetching products:", err);
      } finally {
        setOfficeInventoryLoading(false);
      }
    };

    fetchOfficeProducts();
  }, [token, officeInventoryGlobalFilter, officeInventoryPagination]);

  useEffect(() => {
    const fetchAgentProducts = async () => {
      setAgentInventoryLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/user/agent_inventory/datatable/${firmId}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              page: agentInventoryPagination.pageIndex + 1,
              limit: agentInventoryPagination.pageSize,
              search: agentInventoryGlobalFilter,
              filters: {
                "created_by.id": firmId,
              },
              sort: {
                field: "created_at",
                order: "desc",
              },
            }),
          }
        );

        if (!response.ok) {
          throw new Error(AGENTOFFICEPRODUCTADDEDIT_MESSAGES.fetch.error);
        }

        const result = await response.json();
        console.log("Fetched agent inventory data:", result);
        setAgentProducts(result?.data || []);
        setAgentInventoryRowCount(result?.total || 0);
      } catch (err) {
        messageHandler.product.error("fetch", err);
        setError(AGENTOFFICEPRODUCTADDEDIT_MESSAGES.fetch.error);
        console.error("Error fetching products:", err);
      } finally {
        setAgentInventoryLoading(false);
      }
    };

    fetchAgentProducts();
  }, [token, agentInventoryGlobalFilter, agentInventoryPagination]);

  // Fetch addresses using datatable endpoint
  useEffect(() => {
    const fetchAddresses = async () => {
      setAddressLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/addresses/user/${firmId}/datatable`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              page: addressPagination.pageIndex + 1,
              limit: addressPagination.pageSize,
              search: addressGlobalFilter,
              filters: {},
              sort: {
                field: "created_at",
                order: "desc",
              },
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`Error: ${response.statusText}`);
        }

        const result = await response.json();
        setAddresses(Array.isArray(result?.data) ? result?.data : []);
        setAddressRowCount(result?.total || 0);
      } catch (err) {
        console.error("Error fetching addresses:", err);
        messageHandler.address.error("fetch", err);
        setError(ADDRESSADDEDIT_MESSAGES.fetch.error);
      } finally {
        setAddressLoading(false);
      }
    };

    if (firmId && activeTab === "#address") {
      fetchAddresses();
    }
  }, [token, addressGlobalFilter, addressPagination, firmId, activeTab]);

  const handleActivate = async (licenseId: string) => {
    setDeleting(licenseId); // Set the license being deleted

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/users/${licenseId}/activate`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setAgents(prevAgents =>
        prevAgents?.map(agent =>
          agent.id === licenseId ? { ...agent, LicenseActive: true } : agent
        )
      );


      const result: any = await response.json();
      if (!response.ok || result?.error) {
        messageHandler.agent.error("update", new Error(AGENT_MESSAGES.update.error));
        throw new Error(AGENT_MESSAGES.update.error);
      }
      messageHandler.info("Agent activated successfully!");
      // window.location.reload();
      // navigate("/signage/brokerage/agents", {
      //   state: { successMessage: "Agent activated successfully!" },
      // });
      navigate(0);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An unknown error occurred while activating."
      );
    }
  };

  const handleDeactivate = async (licenseId: string) => {
    setDeleting(licenseId); // Set the license being deleted

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/users/${licenseId}/deactivate`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setAgents(prevAgents =>
        prevAgents?.map(agent =>
          agent.id === licenseId ? { ...agent, LicenseActive: false } : agent
        )
      );
      const result: any = await response.json();
      if (!response.ok || result?.error) {
        messageHandler.agent.error("update", new Error(AGENT_MESSAGES.update.error));
        throw new Error(AGENT_MESSAGES.update.error);
      }
      messageHandler.info("License deactivated successfully!");
      navigate(0);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An unknown error occurred while deactivating."
      );
    }
  };

  const handleTeammemberLicenseActivate = useCallback(
    async (memberId: string) => {
      if (!memberId || !token) return;
      setTeamMemberLicenseToggleId(memberId);
      try {
        const api = await postUserLicenseToggle(token, memberId, "activate");
        if (!api.ok) {
          messageHandler.error("teammember", "update", new Error(api.message));
          return;
        }
        messageHandler.info(api.message);
        setTeammembers((prev) =>
          prev.map((tm) =>
            tm._id === memberId ? { ...tm, LicenseActive: true, status: "Active" } : tm
          )
        );
      } catch (err) {
        messageHandler.error(
          "teammember",
          "update",
          err instanceof Error ? err : new Error("Activation failed")
        );
      } finally {
        setTeamMemberLicenseToggleId(null);
      }
    },
    [token]
  );

  const handleTeammemberLicenseDeactivate = useCallback(
    async (memberId: string) => {
      if (!memberId || !token) return;
      setTeamMemberLicenseToggleId(memberId);
      try {
        const api = await postUserLicenseToggle(token, memberId, "deactivate");
        if (!api.ok) {
          messageHandler.error("teammember", "update", new Error(api.message));
          return;
        }
        messageHandler.info(api.message);
        setTeammembers((prev) =>
          prev.map((tm) =>
            tm._id === memberId ? { ...tm, LicenseActive: false, status: "Inactive" } : tm
          )
        );
      } catch (err) {
        messageHandler.error(
          "teammember",
          "update",
          err instanceof Error ? err : new Error("Deactivation failed")
        );
      } finally {
        setTeamMemberLicenseToggleId(null);
      }
    },
    [token]
  );

  const handlePromoteAsBrokerageAdmin = async (teamMemberId: string) => {
    if (!teamMemberId || !token) return;
    setPromoteLoadingId(teamMemberId);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/users/brokerage-team-members/${teamMemberId}/promote-as-brokerage-admin`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const result: any = await response.json();
      if (!response.ok || result?.error) {
        messageHandler.error("teammember", "update", new Error(result?.message || "Failed to promote as brokerage admin."));
        return;
      }
      messageHandler.info("Team member promoted as brokerage admin successfully.");
      setTeammemberRefreshTrigger((prev) => prev + 1);
      await fetchBrokerageFirmData();
    } catch (err) {
      messageHandler.error("teammember", "update", err instanceof Error ? err : new Error("Failed to promote as brokerage admin."));
    } finally {
      setPromoteLoadingId(null);
    }
  };

  // if (error) {
  //   return <div>Error: {error}</div>;
  // }

  // if (!brokerageFirm) return <div>Loading BrokerageFirm Data...</div>;

  // Format the date to be more human-readable
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleDeleteOffice = async (officeId: string) => {
    setDeleting(officeId);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/offices/${officeId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const result: any = await response.json();
      if (!response.ok || result?.error) {
        const backendMessage = result?.error || result?.message || AGENT_MESSAGES.delete.error;
        messageHandler.error("agent", "delete", new Error(backendMessage));
        throw new Error(backendMessage);
      }
      messageHandler.info("Office deleted successfully!");
      setOffices((prev) => prev?.filter((office) => office._id !== officeId));
    } catch (err) {
      console.error(err instanceof Error ? err.message : "An unknown error occurred while deleting.");
    } finally {
      setDeleting(null);
    }
  };


  // const handleDeleteOffice = async (officeId: string) => {
  //   setDeleting(officeId); // Set the license being deleted

  //   try {
  //     const response = await fetch(
  //       `${process.env.REACT_APP_API_URL}/offices/${officeId}`,
  //       {
  //         method: "DELETE",
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //           "Content-Type": "application/json",
  //         },
  //       }
  //     );

  //     // if (!response.ok) {
  //     //   throw new Error("Failed to delete the Office.");
  //     // }
  //     toast.success("Office deleted successfully!");
  //     // Update the state by removing the deleted license
  //     setDeleting(null);
  //     // navigate(0);
  //   } catch (err) {
  //     toast.error("Failed to delete the Office.");
  //     setError(
  //       err instanceof Error
  //         ? err.message
  //         : "An unknown error occurred while deleting."
  //     );
  //     setDeleting(null);
  //   }finally {
  //           setOffices((prev) => prev?.filter((office) => office._id !== officeId));

  //   }
  // };

  const handleDeleteOfficeProduct = async (officeId: string, productId: string) => {
    setDeleting(productId);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/office/${encodeURIComponent(officeId)}/inventory/${encodeURIComponent(productId)}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const result: any = await response.json();
      if (!response.ok || result?.error) {
        messageHandler.product.error("delete", new Error(AGENTOFFICEPRODUCTADDEDIT_MESSAGES.product.updateFailed));
        throw new Error(AGENTOFFICEPRODUCTADDEDIT_MESSAGES.product.updateFailed);
      }

      messageHandler.product.success("update");
      setOfficeProducts((prev) => prev?.filter((product) => product.product_id !== productId));
    } catch (err) {
      messageHandler.product.error("delete", err);
      setError(AGENTOFFICEPRODUCTADDEDIT_MESSAGES.product.updateFailed);
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteAgent = async (agentId: string) => {
    setDeleting(agentId); // Set the license being deleted

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/users/${agentId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );


      const result: any = await response.json();
      if (!response.ok || result?.error) {
        messageHandler.agent.error("delete", new Error(AGENT_MESSAGES.delete.error));
        throw new Error(AGENT_MESSAGES.delete.error);
      }
      messageHandler.agent.success("delete");
      // Update state immediately
      setAgents((prev) => prev?.filter((agent) => agent.id !== agentId));
      // Reset pagination if current page becomes empty
      setAgentPagination((prev) => {
        const remainingCount = agents.filter((agent) => agent.id !== agentId).length;
        const currentPageStart = prev.pageIndex * prev.pageSize;
        if (remainingCount <= currentPageStart && prev.pageIndex > 0) {
          return { ...prev, pageIndex: prev.pageIndex - 1 };
        }
        return prev;
      });
    } catch (err) {
      console.error(err instanceof Error ? err.message : "An unknown error occurred while deleting.");
      setError(
        err instanceof Error
          ? err.message
          : "An unknown error occurred while deleting."
      );
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteAgentProduct = async (agentId: string, productId: string) => {
    setDeleting(productId);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/agent/${encodeURIComponent(agentId)}/inventory/${encodeURIComponent(productId)}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const result: any = await response.json();
      if (!response.ok || result?.error) {
        messageHandler.product.error("delete", new Error(AGENTOFFICEPRODUCTADDEDIT_MESSAGES.product.updateFailed));
        throw new Error(AGENTOFFICEPRODUCTADDEDIT_MESSAGES.product.updateFailed);
      }

      messageHandler.product.success("update");
      setAgentProducts((prev) =>
        prev?.filter(
          (product) =>
            product.product_id !== productId && product._id !== productId
        )
      );
    } catch (err) {
      messageHandler.product.error("delete", err);
      setError(AGENTOFFICEPRODUCTADDEDIT_MESSAGES.product.updateFailed);
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteAddress = async (addressId: string) => {
    setDeleting(addressId);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/addresses/${addressId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const result: any = await response.json();
      if (!response.ok || result?.error) {
        messageHandler.address.error("delete", new Error(ADDRESSADDEDIT_MESSAGES.update.error));
        throw new Error(ADDRESSADDEDIT_MESSAGES.update.error);
      }
      messageHandler.address.success("delete");
      setAddresses((prev) => prev?.filter((address) => address._id !== addressId));
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An unknown error occurred while deleting."
      );
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteTeammember = async (teammemberId: string) => {
    setDeleting(teammemberId);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/users/${teammemberId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const result: any = await response.json();
      if (!response.ok || result?.error) {
        messageHandler.error("teammember", "delete", new Error(TEAMMEMBER_MESSAGES.delete.error));
        throw new Error(TEAMMEMBER_MESSAGES.delete.error);
      }
      messageHandler.success("teammember", "delete");
      setTeammembers(prev =>
        prev?.filter(teammember => teammember._id !== teammemberId)
      );

      setDeleting(null);

    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An unknown error occurred while deleting."
      );
      setDeleting(null);
    }
  };

  // const handleUnLink = async (productId: string, agentId: string) => {
  //   try {
  //     const response = await fetch(
  //       `${process.env.REACT_APP_API_URL}/linked-installer/agent/${agentId}/product/${productId}/request-unlink`,
  //       {
  //         method: "PUT",
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //         },
  //         body: JSON.stringify({
  //           product: {
  //             linked_mode: "unlock_pending",
  //             locked_status: "locked",
  //           },
  //         }),
  //       }
  //     );

  //    const result: any = await response.json();
  //       if (!response.ok || result?.error) {
  //         const message = result?.error || result?.message || "Failed to save data";
  //         console.error("API Error:", message);
  //         showError(message);
  //         throw new Error(message);
  //       }
  //       showSuccess("Teammember added successfully!");
  //     toast.success(data.message);
  //     navigate(0);
  //   } catch (error) {
  //     console.error("Error canceling product link:", error);
  //   }
  // };

  // const handleCancelLink = async (productId: string, agentId: string) => {
  //   try {
  //     const response = await fetch(
  //       `${process.env.REACT_APP_API_URL}/linked-installer/agent/${agentId}/product/${productId}/cancel`,
  //       {
  //         method: "PUT",
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //         },
  //       }
  //     );

  //     const data = await response.json();
  //     if (!response.ok) {
  //       const errorMessage = data?.error || "Failed to CancelLink product.";
  //       toast.error(errorMessage);
  //       alert(errorMessage);
  //       return;
  //     }

  //     toast.success(data.message);
  //     navigate(0);
  //   } catch (error) {
  //     console.error("Error canceling product link:", error);
  //   }
  // };


  // Teammembers
  // const teammemberColumns = useMemo(() => {
  //   return [
  //     teammemberColumnHelper.display({
  //       id: "serial",
  //       header: "S.No.",
  //       cell: (info) => teammemberPagination.pageIndex * teammemberPagination.pageSize + info.row.index + 1,
  //     }),
  //     teammemberColumnHelper.accessor(
  //       (row: Teammember) => {
  //         const fullName = `${row?.firstName ?? ""} ${row?.lastName ?? ""}`;
  //         return fullName;
  //       },
  //       {
  //         header: "Teammember Name",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //         cell: (info) => {
  //           const name = info.getValue<string>();
  //           const truncated = name?.length > 22 ? `${name.slice(0, 22)}...` : name;
  //           return <span title={name}>{truncated}</span>;
  //         },
  //       }
  //     ),
  //     teammemberColumnHelper.accessor(
  //       (row: Teammember) => row?.jobTitle,
  //       {
  //         header: "Role",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }),
  //     teammemberColumnHelper.accessor(
  //       (row: Teammember) => row?.email,
  //       {
  //         header: "Email",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }),
  //     teammemberColumnHelper.accessor(
  //       (row: Teammember) => row.offices,
  //       {
  //         header: "Assigned Offices",
  //         cell: (info) => {
  //           const offices = info.getValue() as { officeName: string }[];
  //           return offices?.map(o => o.officeName).join(", ");
  //         },
  //         enableGlobalFilter: true,
  //       }
  //     ),
  //     teammemberColumnHelper.accessor("status", {
  //       header: "Status",
  //       cell: (info) => (info.getValue() === 'Active' ? <span className="status-badge status-accepted-btn">Active</span> : <span className="status-badge status-draft-btn">Inactive</span>),
  //       enableGlobalFilter: true,
  //     }),
  //     teammemberColumnHelper.display({
  //       id: "actions",
  //       header: "Actions",
  //       cell: (info) => {
  //         const teammemberInfo = info.row.original;
  //         return (
  //           <>
  //             <Link
  //               to={`/signage/brokerage/editTeammember/${firmId}/${teammemberInfo._id}`}
  //               className="edit me-2"
  //             >
  //               <i className="ri-pencil-fill fs-20"></i>
  //             </Link>
  //             <button
  //               onClick={() =>
  //                 window.confirm("Are you sure you want to delete this team member?") &&
  //                 handleDeleteTeammember(teammemberInfo._id)
  //               }
  //               className="del border-0 p-0"
  //               disabled={deleting === teammemberInfo._id}
  //             >
  //               <i className="ri-delete-bin-4-fill fs-18"></i>
  //             </button>
  //           </>
  //         );
  //       },
  //     }),
  //   ];
  // }, [deleting, handleDeleteTeammember, teammemberGlobalFilter]);

  // useEffect(() => {
  //   const rows: ExportRow[] = teammembers.map(member => ({
  //     "First Name": member.firstName || '',
  //     "Last Name": member.lastName || '',
  //     "Job Role": member.email || '',
  //     "Phone number": member.email || '',
  //     "Email": member.email || '',
  //     "Assgined offices": member.email || '',
  //     "Status": member.status ? 'Active' : 'Inactive',
  //   }));

  //   setExportRows(rows);
  // }, [teammembers]);

  const teammemberColumns = useMemo(() => [
    teammemberColumnHelper.display({
      id: "serial",
      header: () => <div className="th-text S-No-V">S.No.</div>,
      cell: (info) =>
        <div className="text-sm S-No-V">
          {teammemberPagination.pageIndex * teammemberPagination.pageSize + info.row.index + 1}
        </div>,
    }),
    teammemberColumnHelper.accessor(
      (row: Teammember) => {
        const fullName = `${row?.firstName ?? ""} ${row?.lastName ?? ""}`;
        return fullName;
      },
      {
        id: "name",
        header: () => <div className="th-text S-No-V1">Teammember Name</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        cell: (info) => {
          const name = info.getValue<string>();
          const truncated = name?.length > 22 ? `${name.slice(0, 22)}...` : name;
          return <span className="text-sm S-No-V1" title={name}>{truncated}</span>;
        },
      }
    ),
    teammemberColumnHelper.accessor(
      (row: Teammember) => row?.jobTitle,
      {
        id: "role",
        header: () => <div className="th-text">Role</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        cell: info => <span className=" text-sm ">{info.getValue()}</span>,
      }),
    teammemberColumnHelper.accessor(
      (row: Teammember) => row?.email,
      {
        id: "email",
        header: () => <div className="th-text">Email</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        cell: info => <span className="text-sm ">{info.getValue()}</span>,
      }),
    teammemberColumnHelper.accessor(
      (row: Teammember) => row.officeNames || "-",
      {
        id: "offices",
        header: () => <div className="th-text">Assigned Offices</div>,
        cell: (info) => <span className="text-sm">{info.getValue()}</span>,
        enableGlobalFilter: true,
      }
    ),
    teammemberColumnHelper.display({
      id: "makeBrokerageAdmin",
      header: () => <div className="th-text">Make Brokerage Admin</div>,
      cell: (info) => {
        const teammemberInfo = info.row.original;
        const teamMemberId = teammemberInfo._id;
        const isBrokerageFirm = teammemberInfo.licenseType === "BROKERAGE_FIRM";
        const isPromotedAsBrokerageAdmin = Boolean(
          teammemberInfo.is_promoted_as_brokerage_admin
        );
        const isPromoting = promoteLoadingId === teamMemberId;
        if (isBrokerageFirm) return <span className="text-sm text-muted">—</span>;
        if (isPromotedAsBrokerageAdmin) {
          return <span className="text-sm text-muted">—</span>;
        }
        return (
          <button
            type="button"
            className="license-btn license-activate"
            disabled={isPromoting}
            onClick={() => handlePromoteAsBrokerageAdmin(teamMemberId)}
          >
            {isPromoting ? "..." : "Make Admin"}
          </button>
        );
      },
      enableGlobalFilter: false,
    }),
    // teammemberColumnHelper.display({
    //   id: "teamMemberLicense",
    //   header: () => <div className="th-text">License Status</div>,
    //   cell: (info) => {
    //     const teammemberInfo = info.row.original;
    //     const teamMemberId = teammemberInfo._id;
    //     const isBrokerageFirm = teammemberInfo.licenseType === "BROKERAGE_FIRM";
    //     const isActive = isTeamMemberLicenseActive(teammemberInfo);
    //     const isToggling = teamMemberLicenseToggleId === teamMemberId;
    //     if (isBrokerageFirm) return <span className="text-sm text-muted">—</span>;
    //     return (
    //       <div className="d-flex flex-wrap gap-1 align-items-center">
    //         <button
    //           type="button"
    //           className={`license-btn ${isActive ? "license-deactivate" : "license-activate"}`}
    //           disabled={isToggling}
    //           onClick={() =>
    //             isActive
    //               ? void handleTeammemberLicenseDeactivate(teamMemberId)
    //               : void handleTeammemberLicenseActivate(teamMemberId)
    //           }
    //         >
    //           {isToggling ? "..." : isActive ? "Deactivate" : "Activate"}
    //         </button>
    //       </div>
    //     );
    //   },
    //   enableGlobalFilter: false,
    // }),
    teammemberColumnHelper.accessor("status", {
      header: () => <div className="th-text">Status</div>,
      cell: info => {
        const isActive =
          String(info.getValue() || "").trim().toLowerCase() === "active";

        // pick one of two marker-colors
        const className = isActive
          ? "status-radio status-radio-active"
          : "status-radio status-radio-inactive";

        return (
          <div className="d-flex justify-content-center w-100 table-cells text-center">
            <input
              type="radio"
              className={className}
              checked={true}
              readOnly
            />
          </div>
        );
      },
      enableGlobalFilter: false,
    }),
    teammemberColumnHelper.display({
      id: "actions",
      header: () => <div className="th-text">Actions</div>,
      cell: (info) => {
        const teammemberInfo = info.row.original;
        const isBrokerageFirm = teammemberInfo.licenseType === "BROKERAGE_FIRM";
        
        // For BROKERAGE_FIRM, only show Edit button with different route
        if (isBrokerageFirm) {
          return (
            <div className="d-flex align-items-center gap-2">
              {/* Invisible spacer to align edit button with the same position as when all 3 buttons are shown */}
              <div style={{ width: '32px', height: '32px', visibility: 'hidden' }}></div>
              <EditIconButton
                to={`${baseRoute}/brokerageFirm/${teammemberInfo._id}/edit`}
                className="icon-square1"
                title="Edit"
              />
            </div>
          );
        }
        
        // For other types (BROKERAGE_TEAM_MEMBER), show all action buttons
        return (
          <div className="d-flex align-items-center gap-2">
            <ViewIconButton
              to={`${baseRoute}/view-teammember/${firmId}/${teammemberInfo._id}`}
              state={{ type: "office-teammber" }}
              className="btn action-btn "
              title="View"
            />
            <EditIconButton
              to={`${baseRoute}/brokerageTeammember/${firmId}/${teammemberInfo._id}`}
              className="icon-square1"
              title="Edit"
            />
            <DeleteIconButton
              title="Delete"
              className="icon-square1 danger"
              onClick={() => {
                if (!teammemberInfo._id) return;
                setDeleteTarget({
                  type: "teammember",
                  id: teammemberInfo._id,
                  name: `${teammemberInfo.firstName ?? ""} ${teammemberInfo.lastName ?? ""}`.trim() || "Team Member",
                });
              }}
              disabled={deleting === teammemberInfo._id}
            />
          </div>
        );
      },
    }),
  ], [
    baseRoute,
    deleting,
    handleDeleteTeammember,
    teammemberPagination.pageIndex,
    teammemberPagination.pageSize,
    firmId,
    promoteLoadingId,
    teamMemberLicenseToggleId,
    handlePromoteAsBrokerageAdmin,
    handleTeammemberLicenseActivate,
    handleTeammemberLicenseDeactivate,
  ]);


  const teammemberTable = useReactTable({
    data: teammembers ?? [],
    columns: teammemberColumns,
    pageCount: Math.ceil(teammemberRowCount / teammemberPagination.pageSize),
    state: {
      pagination: teammemberPagination,
      globalFilter: teammemberGlobalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setTeammemberPagination,
    onGlobalFilterChange: setTeammemberGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  const teammemberPageCount = teammemberTable.getPageCount();
  const teammemberCurrentPage = teammemberTable.getState().pagination.pageIndex;

  // Offices
  // const officeColumns = useMemo(() => {
  //   return [
  //     officeColumnHelper.display({
  //       id: "serial",
  //       header: "S.No.",
  //       cell: (info) => officePagination.pageIndex * officePagination.pageSize + info.row.index + 1,
  //     }),
  //     // officeColumnHelper.accessor(
  //     //   (row: Office) => row?.officeAddress?.[0]?.label || '',
  //     //   {
  //     //     header: "Label",
  //     //     filterFn: fuzzyTextFilter,
  //     //     enableGlobalFilter: true,
  //     //   }),
  //     officeColumnHelper.accessor(
  //       (row: Office) => row?.officeName || '',
  //       {
  //         header: "Office Name",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }),
  //     // officeColumnHelper.accessor(
  //     //   (row: Office) => row?.officeType || '',
  //     //   {
  //     //     header: "Office Type",
  //     //     filterFn: fuzzyTextFilter,
  //     //     enableGlobalFilter: true,
  //     //   }),
  //     officeColumnHelper.accessor(
  //       (row: Office) => row?.officeAddress?.[0]?.city || '',
  //       {
  //         header: "City",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }),
  //     officeColumnHelper.accessor(
  //       (row: Office) => row?.officeAddress?.[0]?.state || '',
  //       {
  //         header: "State",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }),
  //     officeColumnHelper.accessor(
  //       (row: Office) => row?.officeAddress?.[0]?.country || '',
  //       {
  //         header: "Country",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }),
  //     officeColumnHelper.accessor(
  //       (row: Office) => row?.officeAddress?.[0]?.zip || '',
  //       {
  //         header: "Zip Code",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }),
  //     officeColumnHelper.display({
  //       id: "actions",
  //       header: "Actions",
  //       cell: (info) => {
  //         const officeInfo = info.row.original;
  //         return (
  //           <>
  //             <Link
  //               to={`/signage/brokerage/editOffice/${firmId}/${officeInfo?._id}`}
  //               className="edit me-2"
  //             >
  //               <i className="ri-pencil-fill fs-20"></i>
  //             </Link>

  //             <button
  //               onClick={() =>
  //                 window.confirm(
  //                   "Are you sure you want to delete this office?"
  //                 ) && handleDeleteOffice(officeInfo?._id)
  //               }
  //               className="del border-0 p-0"
  //             >
  //               <i className="ri-delete-bin-4-fill fs-18"></i>
  //             </button>
  //           </>
  //         );
  //       },
  //     }),
  //   ];
  // }, [deleting, handleDeleteOffice, officeGlobalFilter]);

  // useEffect(() => {
  //   const rows: ExportRow[] = teammembers.map(member => ({
  //     "First Name": member.firstName || '',
  //     "Last Name": member.lastName || '',
  //     "Job Role": member.email || '',
  //     "Phone number": member.email || '',
  //     "Email": member.email || '',
  //     "Assgined offices": member.email || '',
  //     "Status": member.status ? 'Active' : 'Inactive',
  //   }));

  //   setExportRows(rows);
  // }, [teammembers]);

  const officeColumns = useMemo(() => [
    officeColumnHelper.display({
      id: "serial",
      header: () => <div className="th-text S-No-V">S.No.</div>,
      cell: (info) => (
        <div className="text-sm S-No-V">
          {officePagination.pageIndex * officePagination.pageSize + info.row.index + 1}
        </div>
      ),
    }),
    // officeColumnHelper.accessor(
    //   (row: Office) => row?.officeAddress?.[0]?.label || '',
    //   {
    //     header: () => <div className="th-text">Label</div>,
    //     filterFn: fuzzyTextFilter,
    //     enableGlobalFilter: true,
    //     cell: info => <span className="table-cells">{info.getValue()}</span>,
    //   }),
    officeColumnHelper.accessor(
      (row: Office) => row?.officeName || '',
      {
        id: "officeName",
        header: () => <div className="th-text S-No-V1">Office Name</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        cell: info => <span className="text-sm S-No-V1">{info.getValue()}</span>,
      }),
    // officeColumnHelper.accessor(
    //   (row: Office) => row?.officeType || '',
    //   {
    //     header: () => <div className="th-text">Office Type</div>,
    //     filterFn: fuzzyTextFilter,
    //     enableGlobalFilter: true,
    //     cell: info => <span className="text-sm">{info.getValue()}</span>,
    //   }),
    officeColumnHelper.accessor(
      (row: Office) => row?.officeAddress?.[0]?.city || '',
      {
        id: "city",
        header: () => <div className="th-text">City</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        cell: info => <span className="text-sm">{info.getValue()}</span>,
      }),
    officeColumnHelper.accessor(
      (row: Office) => row?.officeAddress?.[0]?.state || '',
      {
        id: "state",
        header: () => <div className="th-text">State</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        cell: info => <span className="text-sm">{info.getValue()}</span>,
      }),
    officeColumnHelper.accessor(
      (row: Office) => row?.officeAddress?.[0]?.country || '',
      {
        id: "country",
        header: () => <div className="th-text">Country</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        cell: info => <span className="text-sm">{info.getValue()}</span>,
      }),
    officeColumnHelper.accessor(
      (row: Office) => row?.officeAddress?.[0]?.addressLine1 || '',
      {
        id: "addressLine1",
        header: () => <div className="th-text">Address</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        cell: info => <span className="text-sm">{info.getValue()}</span>,
      }),
    officeColumnHelper.accessor(
      (row: Office) => row?.officeAddress?.[0]?.zip || '',
      {
        id: "zip",
        header: () => <div className="th-text">Zip Code</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        cell: info => <span className="text-sm">{info.getValue()}</span>,
      }),
    officeColumnHelper.display({
      id: "actions",
      header: () => <div className="th-text">Actions</div>,
      cell: (info) => {
        const officeInfo = info.row.original;
        const isDefaultOffice = Boolean(
          (officeInfo as Office & { default_office?: boolean })?.default_office
        );

        return (
          <div className="d-flex align-items-center gap-2">
            {/* <Link
              to={`/signage/view-office/${officeInfo?._id}`}
              className="border-0 p-0 view"
              title="View"
            >
              <i className="ri-eye-fill fs-20"></i>
            </Link> */}
            <EditIconButton
              to={`${baseRoute}/brokerageOffice/${firmId}/${officeInfo?._id}`}
              className="icon-square1" title="Edit"
            />

            {/* {!isDefaultOffice && ( */}
              <DeleteIconButton
                title="Delete"
                className="icon-square1 danger"
                onClick={() => {
                  if (!officeInfo?._id) return;
                  setDeleteTarget({
                    type: "office",
                    id: officeInfo._id,
                    name: officeInfo?.officeName || "Office",
                  });
                }}
                disabled={deleting === officeInfo?._id}
              />
            {/* )} */}
          </div>
        );
      },
    }),
  ], [baseRoute, deleting, handleDeleteOffice, officePagination.pageIndex, officePagination.pageSize, firmId]);


  const officeTable = useReactTable({
    data: offices ?? [],
    columns: officeColumns,
    pageCount: Math.ceil(officeRowCount / officePagination.pageSize),
    state: {
      pagination: officePagination,
      globalFilter: officeGlobalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setOfficePagination,
    onGlobalFilterChange: setOfficeGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  const officePageCount = officeTable.getPageCount();
  const officeCurrentPage = officeTable.getState().pagination.pageIndex;

  // Office inventory
  // const officeInventoryColumns = useMemo(() => {
  //   return [
  //     officeInventoryColumnHelper.display({
  //       id: "serial",
  //       header: "S.No.",
  //       cell: (info) => officeInventoryPagination.pageIndex * officeInventoryPagination.pageSize + info.row.index + 1,
  //     }),
  //     officeInventoryColumnHelper.accessor(
  //       (row: OfficeInventoryObj) => `${row?.office?.officeName}`,
  //       {
  //         header: "Office",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }),
  //     officeInventoryColumnHelper.accessor(
  //       (row: OfficeInventoryObj) => `${row?.product?.name}`,
  //       {
  //         header: "Product Name",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }),
  //     officeInventoryColumnHelper.accessor(
  //       (row: OfficeInventoryObj) => row?.product?.category,
  //       {
  //         header: "Category",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }),
  //     officeInventoryColumnHelper.accessor(
  //       (row: OfficeInventoryObj) => row?.product?.variants?.[0]?.inventory?.[0]?.available,
  //       {
  //         header: "Qty",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }),
  //     officeInventoryColumnHelper.accessor(
  //       (row: OfficeInventoryObj) => row?.product?.status,
  //       {
  //         header: "Status",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }),
  //     officeInventoryColumnHelper.accessor(
  //       (row: OfficeInventoryObj) => row?.product?.linked_installer,
  //       {
  //         header: "Linked Installer",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }),
  //     officeInventoryColumnHelper.display({
  //       id: "actions",
  //       header: "Actions",
  //       cell: (info) => {
  //         const officeInventoryInfo = info.row.original;
  //         return (
  //           <>
  //             <Link
  //               to={`/signage/brokerage/editOfficeProduct/${firmId}/${officeInventoryInfo?.office?._id}/${officeInventoryInfo?.product_id}`}
  //               className="edit me-2"
  //             >
  //               <i className="ri-pencil-fill fs-20"></i>
  //             </Link>
  //             <button
  //               onClick={() =>
  //                 window.confirm("Delete this product?") &&
  //                 handleDeleteOfficeProduct(officeInventoryInfo?.office?._id, officeInventoryInfo?.product_id)
  //               }
  //               className="del border-0 p-0"
  //             >
  //               <i className="ri-delete-bin-4-fill fs-18"></i>
  //             </button>
  //           </>
  //         );
  //       },
  //     }),
  //   ];
  // }, [deleting, handleDeleteOfficeProduct]);

  // Image URL map setup for office products
  const officeProductsAllImageIds = useMemo(() => {
    const ids: string[] = [];
    for (const row of (officeProducts || [])) {
      const id = (row?.product as any)?.images?.[0]?.url as string;
      if (isHexId(id)) ids.push(id.trim());
    }
    return Array.from(new Set(ids));
  }, [officeProducts]);

  const officeProductsDataForMap = useMemo(() => {
    if (officeProductsAllImageIds.length === 0) return [];
    const images = officeProductsAllImageIds.map(url => ({ url }));
    return [
      { product: { variants: [{ images }] } }
    ];
  }, [officeProductsAllImageIds]);

  const officeProductsImageUrlMap = useVariantImageUrlMap(officeProductsDataForMap, token);

  // Image URL map setup for agent products
  const agentProductsAllImageIds = useMemo(() => {
    const ids: string[] = [];
    for (const row of (agentProducts || [])) {
      const id = (row?.product as any)?.images?.[0]?.url as string;
      if (isHexId(id)) ids.push(id.trim());
    }
    return Array.from(new Set(ids));
  }, [agentProducts]);

  const agentProductsDataForMap = useMemo(() => {
    if (agentProductsAllImageIds.length === 0) return [];
    const images = agentProductsAllImageIds.map(url => ({ url }));
    return [
      { product: { variants: [{ images }] } }
    ];
  }, [agentProductsAllImageIds]);

  const agentProductsImageUrlMap = useVariantImageUrlMap(agentProductsDataForMap, token);

  const officeInventoryColumns = useMemo(() => {
    return [
      officeInventoryColumnHelper.display({
        id: "serial",
        header: () => (
          <div className="th-text S-No-V">S.No.</div>
        ),
        cell: (info) => (
          <div className="text-sm S-No-V">
            {officeInventoryPagination.pageIndex * officeInventoryPagination.pageSize + info.row.index + 1}
          </div>
        ),
        enableGlobalFilter: false,
      }),
      officeInventoryColumnHelper.display({
        id: "image",
        header: () => (
          <div className="th-text S-No-V1">Image</div>
        ),
        cell: (info) => {
          const raw = (info.row.original?.product as any)?.images?.[0]?.url as string | undefined;
          let url = "";

          if (raw) {
            if (ABS_URL.test(raw)) {
              // already a full URL
              url = raw;
            } else if (isHexId(raw)) {
              // hex id -> resolve via presigned map / helper
              const map: Record<string, string> = (officeProductsImageUrlMap as any) || {};
              url = map[raw] || getImageSrc(raw, map) || "";
            } else {
              // legacy relative path
              const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
              const path = raw.replace(/^\/+/, "");
              url = `${base}/${encodeURI(path)}`;
            }
          }

          return (
            <div className="text-sm S-No-V1">
              {url ? (
                <img
                  src={url}
                  alt="Product"
                  style={{
                    width: 40,
                    height: 40,
                    objectFit: "contain",
                    borderRadius: 4,
                  }}
                />
              ) : (
                <span>No Image</span>
              )}
            </div>
          );
        },
        enableGlobalFilter: false,
      }),

      officeInventoryColumnHelper.accessor(
        (row: OfficeInventoryObj) => {
          // console.log("brokerage_firm", row?.brokerage_firm);
          return row?.brokerage_firm ?? "";
        },
        {
          id: "Brokerage Name",
          header: () => <div className="th-text">Brokerage Name</div>,
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
          cell: (info) => (
            <span className="text-sm">
              {info.getValue() || "-"}
            </span>
          ),
        }
      ),
      // officeInventoryColumnHelper.accessor(
      //   (row: OfficeInventoryObj) => `${row?.office?.brokerage_firm}`,
      //   {
      //     id: "Brokerage Name",
      //     header: () => <div className="th-text">Brokerage Name</div>,
      //     filterFn: fuzzyTextFilter,
      //     enableGlobalFilter: true,
      //     cell: info => <span className="text-sm">{"-"}</span>,
      //   }),
      officeInventoryColumnHelper.accessor(
        (row: OfficeInventoryObj) => `${row?.office?.officeName}`,
        {
          id: "office",
          header: () => <div className="th-text">Office</div>,
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
          cell: info => <span className="table-cells text-center">{info.getValue()}</span>,
        }),
      officeInventoryColumnHelper.accessor(
        (row: OfficeInventoryObj) => row?.product?.name,
        {
          id: "name",
          header: () => (
            <div className="th-text">Product Name</div>
          ),
          cell: (info) => (
            <div className="text-sm">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      officeInventoryColumnHelper.accessor(
        (row: OfficeInventoryObj) => row.product.sku || "-",
        {
          id: "sku",
          header: () => (
            <div className="th-text">SKU</div>
          ),
          cell: (info) => (
            <div className="text-sm">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      officeInventoryColumnHelper.accessor(
        (row: OfficeInventoryObj) => row.product.category || "-",
        {
          id: "category",
          header: () => (
            <div className="th-text">Category</div>
          ),
          cell: (info) => (
            <div className="text-sm">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      officeInventoryColumnHelper.accessor(
        (row: OfficeInventoryObj) => row.product.variants?.[0]?.inventory?.[0]?.available || 0,
        {
          id: "qty",
          header: () => (
            <div className="th-text">Qty</div>
          ),
          cell: (info) => (
            <div className="text-sm">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      officeInventoryColumnHelper.accessor(
        (row: OfficeInventoryObj) => row.product.item_condition || "-",
        {
          id: "condition",
          header: () => (
            <div className="th-text">Condition</div>
          ),
          cell: (info) => (
            <div className="text-sm">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      // officeInventoryColumnHelper.accessor(
      //   (row: OfficeInventoryObj) => row.created_by?.name || "-",
      //   {
      //     id: "created_by",
      //     header: () => (
      //       <div className="th-text">Linked By</div>
      //     ),
      //     cell: (info) => (
      //       <div className="text-sm">{info.getValue()}</div>
      //     ),
      //     filterFn: fuzzyTextFilter,
      //     enableGlobalFilter: true,
      //   }
      // ),
      officeInventoryColumnHelper.accessor(
        (row: OfficeInventoryObj) =>
          row?.product?.product_location_status?.replace(/_/g, " "),
        {
          id: "product_location_status",
          header: () => (
            <div className="th-text">Handover Method</div>
          ),
          cell: (info) => (
            <div className="text-sm">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      //  columnHelper.display({
      //   id: "SupplierActions",
      //   header: () => (
      //     <div className="th-text">
      //       Supplier Actions
      //     </div>
      //   ),
      //   cell: info => {
      //     const p = info.row.original;
      //    const  product_id = p.product_id
      //    const officeId = p.office._id
      //     return (
      //       <div className="text-sm">
      //         {p.product?.linked_mode === "unlock_pending" ? (
      //           <>
      //             {/* <p className="text-danger mb-0 fw-bold">Unlink Requested</p> */}
      //             <button
      //               className="btn btn-danger btn-sm mb-2"
      //               onClick={() => handleUnlink(p.product_id, officeId)}
      //             >
      //               Approve Unlink
      //             </button>
      //             <textarea
      //               className="form-control"
      //               placeholder="Add Reason..."
      //               value={noteSelections[product_id] ?? ""}
      //               onChange={e => handleNoteChange(product_id, e.target.value)}
      //             />
      //           </>
      //         ) : (
      //           "---"
      //         )}
      //       </div>
      //     );
      //   },
      //   enableGlobalFilter: false,
      // }),
      // officeInventoryColumnHelper.accessor(
      //   (row: OfficeInventoryObj) =>
      //     row.product.current_status?.replace(/_/g, " ") || "-",
      //   {
      //     id: "current_status",
      //     header: () => (
      //       <div className="th-text">
      //         Product location status
      //       </div>
      //     ),
      //     cell: info => (
      //       <div className="text-sm">
      //         {info.getValue()}
      //       </div>
      //     ),
      //     filterFn: fuzzyTextFilter,
      //     enableGlobalFilter: true,
      //   }
      // ),

      officeInventoryColumnHelper.accessor(
        (row: OfficeInventoryObj) =>
          row.product.current_status?.replace(/_/g, " ") || "-",
        {
          id: "current_status",
          header: () => (
            <div className="text-center table-headers">
              Product location status
            </div>
          ),
          cell: info => (
            <div className="text-sm">
              <StatusBadge status={info.getValue()} />
            </div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),

      officeInventoryColumnHelper.accessor(
        (row: OfficeInventoryObj) =>
          row?.product?.locked_status === "unlocked" ? "" : "Linked",
        {
          id: "status",
          header: () => (
            <div className="th-text">Linked Status</div>
          ),
          cell: (info) => (
            <div className="text-sm linked-status">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      officeInventoryColumnHelper.display({
        id: "actions",
        header: () => <div className="th-text">Actions</div>,
        cell: (info) => {
          const officeInventoryInfo = info.row.original;
          return (
            <div className="d-flex align-items-center gap-2">
              <ViewIconButton
                to={`${baseRoute}/BrokerageOfficeProduct/${firmId}/${officeInventoryInfo?.office?._id}/${officeInventoryInfo?.product_id}`}
                className="btn action-btn " title="View"
              />
              <EditIconButton
                to={`${baseRoute}/BrokerageOfficeProduct/${firmId}/${officeInventoryInfo?.office?._id}/${officeInventoryInfo?.product_id}`}
                className="icon-square1" title="Edit"
              />

              <DeleteIconButton
                title="Delete"
                className="icon-square1 danger"
                onClick={() => {
                  const officeId = officeInventoryInfo?.office?._id;
                  if (!officeId || !officeInventoryInfo?.product_id) return;
                  setDeleteTarget({
                    type: "officeInventory",
                    id: officeInventoryInfo.product_id,
                    secondaryId: officeId,
                    name: officeInventoryInfo?.product?.name || "Office Product",
                  });
                }}
                disabled={deleting === officeInventoryInfo?.product_id}
              />
            </div>
          );
        },
      }),
    ];
  }, [baseRoute, deleting, handleDeleteOfficeProduct, officeInventoryPagination.pageIndex, officeInventoryPagination.pageSize, firmId, officeProductsImageUrlMap]);


  // const officeInventoryColumns = useMemo(() => [
  //   officeInventoryColumnHelper.display({
  //     id: "serial",
  //     header: () => <div className="th-text">S.No.</div>,
  //     cell: (info) =>
  //       <div className="text-sm">
  //         {officeInventoryPagination.pageIndex * officeInventoryPagination.pageSize + info.row.index + 1}
  //       </div>,
  //   }),
  //   officeInventoryColumnHelper.accessor(
  //     (row: OfficeInventoryObj) => `${row?.office?.officeName}`,
  //     {
  //       id: "office",
  //       header: () => <div className="th-text">Office</div>,
  //       filterFn: fuzzyTextFilter,
  //       enableGlobalFilter: true,
  //       cell: info => <span className="table-cells">{info.getValue()}</span>,
  //     }),
  //   officeInventoryColumnHelper.accessor(
  //     (row: OfficeInventoryObj) => `${row?.product?.name}`,
  //     {
  //       id: "product",
  //       header: () => <div className="th-text">Product Name</div>,
  //       filterFn: fuzzyTextFilter,
  //       enableGlobalFilter: true,
  //       cell: info => <span className="table-cells">{info.getValue()}</span>,
  //     }),
  //   officeInventoryColumnHelper.accessor(
  //     (row: OfficeInventoryObj) => row?.product?.category,
  //     {
  //       id: "category",
  //       header: () => <div className="th-text">Category</div>,
  //       filterFn: fuzzyTextFilter,
  //       enableGlobalFilter: true,
  //       cell: info => <span className="table-cells">{info.getValue()}</span>,
  //     }),
  //   officeInventoryColumnHelper.accessor(
  //     (row: OfficeInventoryObj) => row?.product?.variants?.[0]?.inventory?.[0]?.available,
  //     {
  //       id: "qty",
  //       header: () => <div className="th-text">Qty</div>,
  //       filterFn: fuzzyTextFilter,
  //       enableGlobalFilter: true,
  //       cell: info => <span className="table-cells">{info.getValue()}</span>,
  //     }),
  //   officeInventoryColumnHelper.accessor(
  //     (row: OfficeInventoryObj) => row?.product?.status,
  //     {
  //       id: "status",
  //       header: () => <div className="th-text">Status</div>,
  //       filterFn: fuzzyTextFilter,
  //       cell: info => {
  //   const value = info.getValue();
  //   const isActive = value === "Active";

  //   // pick one of two marker-colors
  //   const className = isActive
  //     ? "status-radio status-radio-active"
  //     : "status-radio status-radio-inactive";

  //   return (
  //     <div className="d-flex justify-content-center w-100 table-cells">
  //       <input
  //         type="radio"
  //         className={className}
  //         checked={true}
  //         readOnly
  //       />
  //     </div>
  //   );
  // },
  // enableGlobalFilter: false,
  //     }),
  //   officeInventoryColumnHelper.accessor(
  //     (row: OfficeInventoryObj) => row?.product?.linked_installer,
  //     {
  //       id: "linkedInstaller",
  //       header: () => <div className="th-text">Linked Installer</div>,
  //       filterFn: fuzzyTextFilter,
  //       enableGlobalFilter: true,
  //       cell: info => <span className="table-cells">{info.getValue()}</span>,
  //     }),
  //   officeInventoryColumnHelper.display({
  //     id: "actions",
  //     header: () => <div className="th-text">Actions</div>,
  //     cell: (info) => {
  //       const officeInventoryInfo = info.row.original;
  //       return (
  //         <div className="text-sm d-flex gap-2 justify-content-center">
  //           {/* If you have a view page, uncomment below: */}
  //           {/* <Link
  //           to={`/signage/viewOfficeProduct/${firmId}/${officeInventoryInfo?.office?._id}/${officeInventoryInfo?.product_id}`}
  //           className="btn btn-light btn-sm rounded-circle shadow-sm me-3 view"
  //           title="View"
  //         >
  //           <i className="ri-eye-fill fs-20"></i>
  //         </Link> */}
  //           <Link
  //             to={`/signage/brokerage/editOfficeProduct/${firmId}/${officeInventoryInfo?.office?._id}/${officeInventoryInfo?.product_id}`}
  //             className="btn btn-light btn-sm rounded-circle shadow-sm me-3 edit"
  //             title="Edit"
  //           >
  //             <i className="ri-pencil-fill fs-20"></i>
  //           </Link>
  //           <button
  //             className="btn btn-light btn-sm rounded-circle shadow-sm"
  //             title="Delete"
  //             onClick={() =>
  //               window.confirm("Delete this product?") &&
  //               handleDeleteOfficeProduct(officeInventoryInfo?.office?._id, officeInventoryInfo?.product_id)
  //             }
  //             disabled={deleting === officeInventoryInfo?.product_id}
  //           >
  //             <i className="ri-delete-bin-line text-danger fs-18"></i>
  //           </button>
  //         </div>
  //       );
  //     },
  //   }),
  // ], [deleting, handleDeleteOfficeProduct, officeInventoryPagination.pageIndex, officeInventoryPagination.pageSize, firmId]);


  const officeInventoryTable = useReactTable<OfficeInventoryObj>({
    data: officeProducts ?? [],
    columns: officeInventoryColumns,
    pageCount: Math.ceil(officeInventoryRowCount / officeInventoryPagination.pageSize),
    state: {
      pagination: officeInventoryPagination,
      globalFilter: officeInventoryGlobalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setOfficeInventoryPagination,
    onGlobalFilterChange: setOfficeInventoryGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  const officeInventoryPageCount = officeInventoryTable.getPageCount();
  const officeInventoryCurrentPage = officeInventoryTable.getState().pagination.pageIndex;

  // Agents
  // const agentColumns = useMemo(() => {
  //   return [
  //     agentColumnHelper.display({
  //       id: "serial",
  //       header: "S.No.",
  //       cell: (info) => agentPagination.pageIndex * agentPagination.pageSize + info.row.index + 1,
  //     }),
  //     agentColumnHelper.accessor(
  //       (row: Agent) => `${row.firstName} ${row.lastName}`,
  //       {
  //         id: "fullName",
  //         header: "Agent Name",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }
  //     ),
  //     agentColumnHelper.accessor(
  //       (row: Agent) => `${row?.agentDetails?.brokerageFirmAffiliation}`,
  //       {
  //         header: "Brokerage Name",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }),
  //     agentColumnHelper.accessor(
  //       (row: Agent) => `${row?.offices?.[0]?.officeName}`,
  //       {
  //         header: "Office Name",
  //         cell: (info) => info?.row?.original?.offices ? info?.row?.original?.offices?.[0]?.officeName : "",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }),
  //     agentColumnHelper.accessor(
  //       (row: Agent) => formatDate(row.created_at),
  //       {
  //         header: "Date Joined",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }
  //     ),
  //     agentColumnHelper.accessor(
  //       (row: Agent) => row.email,
  //       {
  //         header: "Email",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }
  //     ),
  //     agentColumnHelper.accessor(
  //       (row: Agent) => row.phone1 || row.phone2,
  //       {
  //         header: "Phone",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }
  //     ),
  //     agentColumnHelper.accessor(
  //       (row: Agent) => row.status,
  //       {
  //         header: "Status",
  //         enableGlobalFilter: true,
  //       }
  //     ),
  //     agentColumnHelper.display({
  //       id: "action",
  //       header: "Action",
  //       cell: (info) => {
  //         const agentInfo = info.row.original;
  //         return (
  //           <>
  //             {agentInfo.LicenseActive ? (
  //               <button
  //                 className="btn btn-danger btn-sm m-1"
  //                 onClick={() => handleDeactivate(agentInfo.id)}
  //               >
  //                 Deactivate
  //               </button>
  //             ) : (
  //               <button
  //                 className="btn btn-success btn-sm m-1"
  //                 onClick={() => handleActivate(agentInfo.id)}
  //               >
  //                 Activate
  //               </button>
  //             )}
  //           </>
  //         );
  //       },
  //     }),
  //     agentColumnHelper.display({
  //       id: "actions",
  //       header: "Actions",
  //       cell: (info) => {
  //         const agentInfo = info.row.original;
  //         return (
  //           <div className="d-flex gap-2">
  //             {/* <Link
  //               to={`/signage/brokerage/viewAgent/${agentInfo.id}`}
  //               className="view me-2"
  //             >
  //               <i className="ri-eye-fill fs-20"></i>
  //             </Link> */}

  //             <Link
  //               to={`/signage/brokerage/editAgent/${firmId}/${agentInfo.id}`}
  //               className="edit me-2"
  //             >
  //               <i className="ri-pencil-fill fs-20"></i>
  //             </Link>

  //             <Link
  //               to="#"
  //               onClick={() =>
  //                 window.confirm(
  //                   "Are you sure you want to delete this agent?"
  //                 ) && handleDeleteAgent(agentInfo.id)
  //               }
  //               className="del border-0 p-0"
  //             >
  //               <i className="ri-delete-bin-4-fill fs-18"></i>
  //             </Link>
  //           </div>
  //         );
  //       },
  //     }),
  //   ];
  // }, [deleting, handleActivate, handleDeactivate, handleDeleteAgent]);

  const agentColumns = useMemo(() => [
    agentColumnHelper.display({
      id: "serial",
      header: () => <div className="th-text S-No-V">S.No.</div>,
      cell: (info) => (
        <div className="text-sm S-No-V">
          {agentPagination.pageIndex * agentPagination.pageSize + info.row.index + 1}
        </div>
      ),
    }),
    agentColumnHelper.accessor(
      (row: Agent) => `${row.firstName} ${row.lastName}`,
      {
        id: "fullName",
        header: () => <div className="th-text S-No-V1">Agent Name</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        cell: info => <span className="text-sm S-No-V1">{info.getValue()}</span>,
      }
    ),
    agentColumnHelper.accessor(
      (row: Agent) => `${row?.agentDetails?.brokerageFirmAffiliation}`,
      {
        id: "brokerageName",
        header: () => <div className="th-text">Brokerage Name</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        cell: info => <span className="text-sm">{info.getValue()}</span>,
      }),
    agentColumnHelper.accessor(
      (row: Agent) => `${row?.offices?.[0]?.officeName}`,
      {
        id: "officeName",
        header: () => <div className="th-text">Office Name</div>,
        cell: (info) => <span className="text-sm">{info?.row?.original?.offices?.[0]?.officeName ?? ""}</span>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
    agentColumnHelper.accessor(
      (row: Agent) => formatDate(row.created_at),
      {
        id: "dateJoined",
        header: () => <div className="th-text">Date Joined</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        cell: info => <span className="text-sm">{info.getValue()}</span>,
      }
    ),
    agentColumnHelper.accessor(
      (row: Agent) => row.email,
      {
        id: "email",
        header: () => <div className="th-text">Email</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        cell: info => <span className="text-sm">{info.getValue()}</span>,
      }
    ),
    agentColumnHelper.accessor(
      (row: Agent) => row.phone1 || row.phone2,
      {
        id: "phone",
        header: () => <div className="th-text">Phone</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        cell: info => <span className="text-sm">{info.getValue()}</span>,
      }
    ),
    // agentColumnHelper.display({
    //   id: "licensestatus",
    //   header: () => <div className="th-text">License Status</div>,
    //   cell: (info) => {
    //     const agentInfo = info.row.original;
    //     const isActive = agentInfo.LicenseActive;
    //     const id = agentInfo.id || agentInfo._id;
    //     const buttonClass = isActive
    //       ? "license-btn license-deactivate"
    //       : "license-btn license-activate";

    //     return (
    //       <div className="text-sm">
    //         <button
    //           className={buttonClass}
    //           onClick={() =>
    //             isActive ? handleDeactivate(id) : handleActivate(id)
    //           }
    //         >
    //           {isActive ? "Deactivate" : "Activate"}
    //         </button>
    //       </div>
    //     );
    //   },
    // }),
    agentColumnHelper.accessor(
      (row: Agent) => row.status,
      {
        id: "status",
        header: () => <div className="th-text">Status</div>,
        cell: info => {
          const value = info.getValue();
          const isActive = value === "Active";

          // pick one of two marker-colors
          const className = isActive
            ? "status-radio status-radio-active"
            : "status-radio status-radio-inactive";

          return (
            <div className="d-flex justify-content-center w-100 table-cells">
              <input
                type="radio"
                className={className}
                checked={true}
                readOnly
              />
            </div>
          );
        },
        enableGlobalFilter: false,
      }
    ),
    // agentColumnHelper.display({
    //   id: "action",
    //   header: () => <div className="th-text">Action</div>,
    //   cell: (info) => {
    //     const agentInfo = info.row.original;
    //     return (
    //       <div className="text-sm d-flex gap-2 justify-content-center">
    //         {agentInfo.LicenseActive ? (
    //           <button
    //             className="btn btn-light btn-sm rounded-circle shadow-sm me-3"
    //             title="Deactivate"
    //             onClick={() => handleDeactivate(agentInfo.id)}
    //           >
    //             <i className="ri-close-line text-danger fs-20"></i>
    //           </button>
    //         ) : (
    //           <button
    //             className="btn btn-light btn-sm rounded-circle shadow-sm me-3"
    //             title="Activate"
    //             onClick={() => handleActivate(agentInfo.id)}
    //           >
    //             <i className="ri-check-line text-success fs-20"></i>
    //           </button>
    //         )}
    //       </div>
    //     );
    //   },
    // }),
    agentColumnHelper.display({
      id: "actions",
      header: () => <div className=" th-text">Actions</div>,
      cell: (info) => {
        const agentInfo = info.row.original;
        const id = agentInfo.id || agentInfo._id;
        return (
          <div className="d-flex align-items-center gap-2">
            {/* If you want the view button, uncomment this: */}
            {/* <Link
            to={`/signage/brokerage/viewAgent/${agentInfo.id}`}
            className="btn btn-light btn-sm rounded-circle shadow-sm me-3 view"
            title="View"
          >
            <i className="ri-eye-fill fs-20"></i>
          </Link> */}
            <EditIconButton
              to={`${baseRoute}/brokerageAgent/${firmId}/${id}`}
              className="icon-square1" title="Edit"
            />
            <DeleteIconButton
              title="Delete"
              className="icon-square1 danger"
              onClick={() => {
                if (!id) return;
                setDeleteTarget({
                  type: "agent",
                  id,
                  name: `${agentInfo.firstName ?? ""} ${agentInfo.lastName ?? ""}`.trim() || "Agent",
                });
              }}
              disabled={deleting === id}
            />
          </div>
        );
      },
    }),
  ], [baseRoute, deleting, handleActivate, handleDeactivate, handleDeleteAgent, agentPagination.pageIndex, agentPagination.pageSize, firmId]);


  const agentTable = useReactTable({
    data: agents ?? [],
    columns: agentColumns,
    pageCount: Math.ceil(agentRowCount / agentPagination.pageSize),
    state: {
      pagination: agentPagination,
      globalFilter: agentGlobalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setAgentPagination,
    onGlobalFilterChange: setAgentGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });
  const agentInventoryColumns = useMemo(() => {
    return [
      // --- S.No. ---
      agentInventoryColumnHelper.display({
        id: "serial",
        header: () => (
          <div className="text-center table-headers">
            S.No.
          </div>
        ),
        cell: info => (
          <div className="text-sm">
            {agentInventoryPagination.pageIndex * agentInventoryPagination.pageSize + info.row.index + 1}
          </div>
        ),
        enableGlobalFilter: false,
      }),

      // --- Image ---
      // --- Image ---
      agentInventoryColumnHelper.display({
        id: "image",
        header: () => (
          <div className="text-center table-headers">
            Image
          </div>
        ),
        cell: info => {
          const raw = (info.row.original?.product as any)?.images?.[0]?.url as string | undefined;
          let url = "";

          if (raw) {
            if (ABS_URL.test(raw)) {
              // already a full URL
              url = raw;
            } else if (isHexId(raw)) {
              // hex id -> resolve via presigned map / helper
              const map: Record<string, string> = (agentProductsImageUrlMap as any) || {};
              url = map[raw] || getImageSrc(raw, map) || "";
            } else {
              // legacy relative path
              const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
              const path = raw.replace(/^\/+/, "");
              url = `${base}/${encodeURI(path)}`;
            }
          }

          return (
            <div className="text-sm">
              {url ? (
                <img
                  src={url}
                  alt="Product"
                  style={{
                    width: 40,
                    height: 40,
                    objectFit: "contain",
                    borderRadius: 4,
                  }}
                />
              ) : (
                <span>No Image</span>
              )}
            </div>
          );
        },
        enableGlobalFilter: false,
      }),
      agentInventoryColumnHelper.accessor(
        (row: AgentProduct) => row.agent_name,
        {
          id: "agent_name",
          header: () => (
            <div className="text-center table-headers">
              Agent Name
            </div>
          ),
          cell: info => (
            <div className="text-sm">
              {info.getValue() || "-"}
            </div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),

      agentInventoryColumnHelper.accessor(
        (row: AgentProduct) => row.agent_email,
        {
          id: "agent_email",
          header: () => (
            <div className="text-center table-headers">
              Agent Email
            </div>
          ),
          cell: info => (
            <div className="text-sm">
              {info.getValue() || "-"}
            </div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),


      // --- Product Name ---
      agentInventoryColumnHelper.accessor(
        (row: AgentProduct) => row.product.name,
        {
          id: "name",
          header: () => (
            <div className="text-center table-headers">
              Product Name
            </div>
          ),
          cell: info => (
            <div className="text-sm">
              {info.getValue()}
            </div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),

      // --- SKU ---
      agentInventoryColumnHelper.accessor(
        (row: AgentProduct) => row.product.sku || "-",
        {
          id: "sku",
          header: () => (
            <div className="text-center table-headers">
              SKU
            </div>
          ),
          cell: info => (
            <div className="text-sm">
              {info.getValue()}
            </div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),

      // --- Category ---
      agentInventoryColumnHelper.accessor(
        (row: AgentProduct) => row.product.category || "-",
        {
          id: "category",
          header: () => (
            <div className="text-center table-headers">
              Category
            </div>
          ),
          cell: info => (
            <div className="text-sm">
              {info.getValue()}
            </div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: false,
        }
      ),

      // --- Qty ---
      agentInventoryColumnHelper.accessor(
        (row: AgentProduct) =>
          row.product.variants?.[0]?.inventory?.[0]?.available ?? 0,
        {
          id: "qty",
          header: () => (
            <div className="text-center table-headers">
              Qty
            </div>
          ),
          cell: info => (
            <div className="text-sm">
              {info.getValue()}
            </div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: false,
        }
      ),

      // --- Condition ---
      agentInventoryColumnHelper.accessor(
        (row: AgentProduct) => row.product.item_condition || "-",
        {
          id: "condition",
          header: () => (
            <div className="text-center table-headers">
              Condition
            </div>
          ),
          cell: info => (
            <div className="text-sm">
              {info.getValue()}
            </div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: false,
        }
      ),

      // --- Linked By ---
      // agentInventoryColumnHelper.accessor(
      //   (row: AgentProduct) => row.created_by?.name || "-",
      //   {
      //     id: "linked_by",
      //     header: () => (
      //       <div className="text-center table-headers">
      //         Linked By
      //       </div>
      //     ),
      //     cell: info => (
      //       <div className="text-sm">
      //         {info.getValue()}
      //       </div>
      //     ),
      //     filterFn: fuzzyTextFilter,
      //     enableGlobalFilter: true,
      //   }
      // ),
      agentInventoryColumnHelper.accessor(
        (row: AgentProduct) =>
          row?.product?.product_location_status?.replace(/_/g, " "),
        {
          id: "product_location_status",
          header: () => (
            <div className="text-center table-headers">Handover Method</div>
          ),
          cell: (info) => (
            <div className="text-sm">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      //   columnHelper.display({
      //   id: "SupplierActions",
      //   header: () => (
      //     <div className="text-center table-headers">
      //       Supplier Actions
      //     </div>
      //   ),
      //   cell: info => {
      //     const p = info.row.original;
      //    const  product_id = p.product_id
      //    const officeId = p.office._id
      //     return (
      //       <div className="text-sm">
      //         {p.product?.linked_mode === "unlock_pending" ? (
      //           <>
      //             {/* <p className="text-danger mb-0 fw-bold">Unlink Requested</p> */}
      //             <button
      //               className="btn btn-danger btn-sm mb-2"
      //               onClick={() => handleUnlink(p.product_id, officeId)}
      //             >
      //               Approve Unlink
      //             </button>
      //             <textarea
      //               className="form-control"
      //               placeholder="Add Reason..."
      //               value={noteSelections[product_id] ?? ""}
      //               onChange={e => handleNoteChange(product_id, e.target.value)}
      //             />
      //           </>
      //         ) : (
      //           "---"
      //         )}
      //       </div>
      //     );
      //   },
      //   enableGlobalFilter: false,
      // }),

      // --- Product Location Status ---
      // agentInventoryColumnHelper.display({
      //   id: "SupplierActions",
      //   header: () => (
      //     <div className="text-center table-headers">
      //       Supplier Actions
      //     </div>
      //   ),
      //   cell: info => {
      //     const p = info.row.original;
      //     return (
      //       <div className="text-sm">
      //         {p.product.linked_mode === "unlock_pending" && (
      //           <>
      //             <p className="text-danger mb-0 fw-bold">
      //               Unlink Requested
      //             </p>
      //             {/* <button
      //               className="btn btn-danger btn-sm mb-2"
      //               onClick={() =>
      //                 handleUnlink(p.product_id, p.agent_id)
      //               }
      //             >
      //               Approve Unlink
      //             </button> */}
      //             {/* <textarea
      //               className="form-control"
      //               placeholder="Add Reason..."
      //               value={noteSelections[p.product_id] ?? ""}
      //               onChange={e =>
      //                 handleNoteChange(p.product_id, e.target.value)
      //               }
      //             /> */}
      //           </>
      //         )}
      //       </div>
      //     );
      //   },
      //   enableGlobalFilter: false,
      // }),

      // --- Current Status ---
      // agentInventoryColumnHelper.accessor(
      //   (row: AgentProduct) =>
      //     row.product.current_status?.replace(/_/g, " ") || "-",
      //   {
      //     id: "current_status",
      //     header: () => (
      //       <div className="text-center table-headers">
      //         Product location status
      //       </div>
      //     ),
      //     cell: info => (
      //       <div className="text-sm">
      //         {info.getValue()}
      //       </div>
      //     ),
      //     filterFn: fuzzyTextFilter,
      //     enableGlobalFilter: true,
      //   }
      // ),

      agentInventoryColumnHelper.accessor(
        (row: AgentProduct) =>
          row.product.current_status?.replace(/_/g, " ") || "-",
        {
          id: "current_status",
          header: () => (
            <div className="text-center table-headers">
              Product location status
            </div>
          ),
          cell: info => (
            <div className="text-sm">
              <StatusBadge status={info.getValue()} />
            </div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),

      // --- Linked Status ---
      agentInventoryColumnHelper.accessor(
        (row: AgentProduct) =>
          row.product.locked_status === "unlocked" ? "" : "Linked",
        {
          id: "linked_status",
          header: () => (
            <div className="text-center table-headers">
              Linked Status
            </div>
          ),
          cell: info => (
            <div className="text-sm linked-status">
              {info.getValue()}
            </div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),

      // --- Actions ---
      agentInventoryColumnHelper.display({
        id: "actions",
        header: () => <div className="th-text">Actions</div>,
        cell: (info) => {
          const agentproduct = info.row.original;
          const agentId = String(agentproduct.agent_id || "").trim();
          const productId = String(
            agentproduct.product_id || agentproduct._id || ""
          ).trim();
          return (
            <div className="d-flex align-items-center gap-2">
              <ViewIconButton
                to={`${baseRoute}/BrokerageAgentProduct/${firmId}/${agentId}/${productId}`}
                className="btn action-btn " title="View"
              />
              <EditIconButton
                to={`${baseRoute}/BrokerageAgentProduct/${firmId}/${agentId}/${productId}`}
                className="icon-square1" title="Edit"
              />
              <DeleteIconButton
                title="Delete"
                className="icon-square1 danger"
                onClick={() => {
                  if (!productId || !agentId) return;
                  setDeleteTarget({
                    type: "agentInventory",
                    id: productId,
                    secondaryId: agentId,
                    name: agentproduct.product?.name || "Agent Product",
                  });
                }}
                disabled={deleting === productId || deleting === agentproduct._id}
              />
            </div>
          );
        },
        enableGlobalFilter: false,
      }),
    ];
  }, [baseRoute, deleting, handleDeleteAgentProduct, agentInventoryPagination.pageIndex, agentInventoryPagination.pageSize, firmId, agentProductsImageUrlMap]);

  const agentInventoryTable = useReactTable({
    data: agentProducts ?? [],
    columns: agentInventoryColumns,
    pageCount: Math.ceil(agentInventoryRowCount / agentInventoryPagination.pageSize),
    state: {
      pagination: agentInventoryPagination,
      globalFilter: agentInventoryGlobalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setAgentInventoryPagination,
    onGlobalFilterChange: setAgentInventoryGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  // Address columns
  const addressColumns = useMemo(() => [
    addressColumnHelper.display({
      id: "serial",
      header: () => <div className="th-text S-No-V">S.No.</div>,
      cell: (info) => (
        <div className="text-sm S-No-V">
          {addressPagination.pageIndex * addressPagination.pageSize + info.row.index + 1}
        </div>
      ),
    }),
    addressColumnHelper.accessor("label", {
      id: "label",
      header: () => <div className="th-text S-No-V1">Label</div>,
      filterFn: fuzzyTextFilter,
      enableGlobalFilter: true,
      cell: info => <span className="text-sm S-No-V1">{info.getValue()}</span>,
    }),
    addressColumnHelper.display({
      id: "offices",
      header: () => <div className="th-text">Offices</div>,
      cell: (info) => {
        const address = info.row.original;
        // Show office name if user_type is OFFICE
        if (address.user_type === "OFFICE" ||address.user_type === "office") {
          const officeName = address.first_name || "-";
          return <span className="text-sm">{officeName}</span>;
        }
        return <span className="text-sm">-</span>;
      },
      enableGlobalFilter: true,
    }),
    addressColumnHelper.display({
      id: "agents",
      header: () => <div className="th-text">Agents</div>,
      cell: (info) => {
        const address = info.row.original;
        // Show agent name if user_type is BROKERAGE_AGENT
        if (address.user_type === "BROKERAGE_AGENT") {
          const agentName = `${address.first_name || ""} ${address.last_name || ""}`.trim() || "-";
          return <span className="text-sm">{agentName}</span>;
        }
        return <span className="text-sm">-</span>;
      },
      enableGlobalFilter: true,
    }),
    addressColumnHelper.display({
      id: "address",
      header: () => <div className="th-text">Address</div>,
      cell: (info) => {
        const address = info.row.original;
        const addressDetails = address.address_details || (address as any).addressDetails || {};
        // Handle both camelCase and snake_case formats
        const addressLine1 = addressDetails.addressLine1 || "";
        return <span className="text-sm">{addressLine1 || "-"}</span>;
      },
      filterFn: fuzzyTextFilter,
      enableGlobalFilter: true,
    }),
    addressColumnHelper.accessor("address_details.city", {
      id: "city",
      header: () => <div className="th-text">City</div>,
      filterFn: fuzzyTextFilter,
      enableGlobalFilter: true,
      cell: info => <span className="text-sm">{info.getValue() || "-"}</span>,
    }),
    addressColumnHelper.accessor("address_details.state", {
      id: "state",
      header: () => <div className="th-text">State</div>,
      filterFn: fuzzyTextFilter,
      enableGlobalFilter: true,
      cell: info => <span className="text-sm">{info.getValue() || "-"}</span>,
    }),
    addressColumnHelper.accessor("address_details.country", {
      id: "country",
      header: () => <div className="th-text">Country</div>,
      filterFn: fuzzyTextFilter,
      enableGlobalFilter: true,
      cell: info => <span className="text-sm">{info.getValue() || "-"}</span>,
    }),
    addressColumnHelper.accessor("address_details.zip", {
      id: "zipCode",
      header: () => <div className="th-text">Zip Code</div>,
      filterFn: fuzzyTextFilter,
      enableGlobalFilter: true,
      cell: info => <span className="text-sm">{info.getValue() || "-"}</span>,
    }),
    addressColumnHelper.accessor("default_address", {
      id: "default",
      header: () => <div className="th-text">Default</div>,
      cell: info => {
        const isDefault = info.getValue();
        return (
          <div className="d-flex justify-content-center w-100 table-cells text-center">
            {/* <span className={isDefault ? "badge bg-success" : "badge bg-secondary"}> */}
            {isDefault ? "Yes" : "No"}
            {/* </span> */}
          </div>
        );
      },
      enableGlobalFilter: false,
    }),
    addressColumnHelper.display({
      id: "actions",
      header: () => <div className="th-text">Action</div>,
      cell: (info) => {
        const addressInfo = info.row.original;
        return (
          <div className="d-flex align-items-center gap-2">
            <EditIconButton
              title="Edit"
              className="icon-square1"
              onClick={() => {
                setEditingAddress(addressInfo);
                setShowAddressModal(true);
              }}
            />
            <DeleteIconButton
              title="Delete"
              className="icon-square1 danger"
              onClick={() => {
                if (!addressInfo._id) return;
                setDeleteTarget({
                  type: "address",
                  id: addressInfo._id,
                  name: addressInfo.label || "Address",
                });
              }}
              disabled={deleting === addressInfo._id}
            />
          </div>
        );
      },
    }),
  ], [deleting, addressPagination.pageIndex, addressPagination.pageSize]);

  const addressTable = useReactTable({
    data: addresses ?? [],
    columns: addressColumns,
    pageCount: Math.ceil(addressRowCount / addressPagination.pageSize),
    state: {
      pagination: addressPagination,
      globalFilter: addressGlobalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setAddressPagination,
    onGlobalFilterChange: setAddressGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  const addressPageCount = addressTable.getPageCount();
  const addressCurrentPage = addressTable.getState().pagination.pageIndex;

  useEffect(() => {
    const handleHashChange = () => {
      setActiveTab(window.location.hash || "#teammembers");
    };

    window.addEventListener("hashchange", handleHashChange);
    handleHashChange(); // handle initial load

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  const TAB_TABLES: Record<string, { table: any; name: string }> = {
    "#teammembers": { table: teammemberTable, name: "Team Members" },
    "#offices": { table: officeTable, name: "Offices" },
    "#officeInventory": { table: officeInventoryTable, name: "Office Inventory" },
    "#agents": { table: agentTable, name: "Agents" },
    "#agentInventory": { table: agentInventoryTable, name: "Agent Inventory" },
    "#address": { table: addressTable, name: "Address" },
  };

  const activeEntry = TAB_TABLES[activeTab];


  // Build a tiny payload just for presigning this single image id
  const dataForMap = React.useMemo(() => {
    const id = (brokerageFirm?.profilePic || "").trim();
    return id ? [{ product: { variants: [{ images: [{ url: id }] }] } }] : [];
  }, [brokerageFirm?.profilePic]);

  // Get the presigned URL map
  const imageUrlMap = useVariantImageUrlMap(dataForMap, token || null);

  // Resolve final avatar URL from id (or fall back if needed)
  const avatarUrl: string | undefined =
    getImageSrc(brokerageFirm?.profilePic, imageUrlMap) ||
    (typeof brokerageFirm?.profilePic === "string" && brokerageFirm?.profilePic.startsWith("http")
      ? brokerageFirm.profilePic
      : undefined);

  // Initials fallback (what you already show as "SF")
  const initialsText =
    `${(brokerageFirm?.firstName ?? "").slice(0, 1)}${(brokerageFirm?.lastName ?? "").slice(0, 1)}`.toUpperCase() || "—";


  // useEffect(() => {
  //   fetchAll();
  // }, [token, fetchAll]);

  const handleDeleteCancel = () => {
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    try {
      switch (deleteTarget.type) {
        case "teammember":
          await handleDeleteTeammember(deleteTarget.id);
          break;
        case "office":
          await handleDeleteOffice(deleteTarget.id);
          break;
        case "officeInventory":
          await handleDeleteOfficeProduct(deleteTarget.secondaryId, deleteTarget.id);
          break;
        case "agent":
          await handleDeleteAgent(deleteTarget.id);
          break;
        case "agentInventory":
          await handleDeleteAgentProduct(deleteTarget.secondaryId, deleteTarget.id);
          break;
        case "address":
          await handleDeleteAddress(deleteTarget.id);
          break;
        default:
          break;
      }
    } finally {
      setDeleteTarget(null);
    }
  };

  // ===== Delete =====
  // const handleDelete = async (id: string) => {


  return (
    <div className="content">
      <div className="container py-3">
        {/* Back */}
        <button
          type="button"
          className="btn btn-light d-inline-flex align-items-center gap-2 br-8 px-3 mb-3"
          onClick={() => navigate(-1)}
        >
          <i className="ri-arrow-left-line" />
          Back
        </button>

        <EntityProfileViewCard
          name={brokerageFirm?.companyName || "---"}
          subtitle="Brokerage Firm Details"
          status={brokerageFirm?.status}
          editHref={`${baseRoute}/brokerageFirm/${firmId}/edit`}
          avatarUrl={avatarUrl}
          initials={initialsText}
          fields={[
            { label: "Company Name", value: brokerageFirm?.companyName },
            { label: "First Name", value: brokerageFirm?.firstName },
            { label: "Last Name", value: brokerageFirm?.lastName },
            {
              label: "Email",
              value: brokerageFirm?.email ? (
                <a href={`mailto:${brokerageFirm.email}`} className="re-profile-card__link">
                  {brokerageFirm.email}
                </a>
              ) : undefined,
            },
            { label: "Phone Number", value: brokerageFirm?.phone1 },
            { label: "Secondary Phone Number", value: brokerageFirm?.phone2 },
          ]}
        />
      </div>
      <div className="container py-3">
        {/* Tabs */}


        <div className="tab-content text-muted box-shadow-datatables shadow-border-datatables ">
          <ul className="nav nav-tabs ms-3 mt-4" role="tablist">
            <li className="nav-item">
              <button type="button" className={`nav-link-soft text-sm  ${activeTab === "#teammembers" ? "active" : ""}`} role="tab" onClick={() => setActiveTab("#teammembers")}>
                <i className="bi bi-people me-2" /> Team members
              </button>
            </li>
            <li className="nav-item">
              <button type="button" className={`nav-link-soft text-sm ${activeTab === "#offices" ? "active" : ""}`} role="tab" onClick={() => setActiveTab("#offices")}>
                <i className="bi bi-building me-2" /> Offices
              </button>
            </li>
            <li className="nav-item">
              <button type="button" className={`nav-link-soft text-sm ${activeTab === "#officeInventory" ? "active" : ""}`} role="tab" onClick={() => setActiveTab("#officeInventory")}>
                <i className="bi bi-box-seam me-2" /> Office Inventory
              </button>
            </li>
            <li className="nav-item">
              <button type="button" className={`nav-link-soft text-sm ${activeTab === "#agents" ? "active" : ""}`} role="tab" onClick={() => setActiveTab("#agents")}>
                <i className="bi bi-person-badge me-2" /> Agents
              </button>
            </li>
            <li className="nav-item">
              <button type="button" className={`nav-link-soft text-sm ${activeTab === "#agentInventory" ? "active" : ""}`} role="tab" onClick={() => setActiveTab("#agentInventory")}>
                <i className="bi bi-archive me-2" /> Agent Inventory
              </button>
            </li>
            <li className="nav-item">
              <button type="button" className={`nav-link-soft text-sm ${activeTab === "#address" ? "active" : ""}`} role="tab" onClick={() => setActiveTab("#address")}>
                <i className="bi bi-geo-alt me-2" /> Address
              </button>
            </li>
          </ul>
          {/* ================= TEAM MEMBERS ================= */}
          <div className={`tab-pane ${activeTab === "#teammembers" ? "active" : ""}`} id="teammembers" role="tabpanel">
            <div className="">
              <div >

                {/* INNER PANEL (exact like screenshot) */}
                <div className="panel shadow-border-datatables box-shadow-datatables m-4">
                  {/* Title line */}
                  <div className="panel-head px-4 py-3 border-bottom">
                    <h5 className="m-0 view-cards">Team Member Management</h5>
                  </div>

                  {/* Search (left) + Add button (right) line */}
                  <div className="panel-toolbar px-4 py-3 border-bottom d-flex align-items-center justify-content-between gap-3 flex-wrap">
                    <div className="input-group tm-search">
                      <span className="input-group-text"><i className="bi bi-search" /></span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by Name and Email"
                        value={teammembersSearchInput}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setTeammembersSearchInput(e.target.value)}
                        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                          if (e.key === "Enter") {
                            setTeammemberGlobalFilter(teammembersSearchInput);
                          }
                        }}
                      />
                    </div>

                    <Link to={`${baseRoute}/brokerageTeammember/${firmId}`} className="btn btn-primary table-addbutton d-inline-flex align-items-center">
                      <i className="bi bi-plus-lg me-2" />
                      Add Team Member
                    </Link>
                  </div>

                  {/* TABLE (sticky header + scroll) */}
                  <div className="rdt-desktop">
                    {teammemberLoading ? (
                      <div className="panel-table">
                        <div className="table-wrap " style={{ overflowX: "auto" }}>
                          <table className="table production-table align-middle mb-0 table-1">
                            <DesktopTableLoader table={teammemberTable} message="Loading team members…" />
                          </table>
                        </div>
                      </div>
                    ) : teammemberTable?.getRowModel().rows.length ? (
                      <>
                        <div className="panel-table">
                          <div className="table-wrap " style={{ overflowX: "auto" }}>
                            <DataTable table={teammemberTable} />
                          </div>
                        </div>
                        {activeEntry && (
                          <PaginationControlsView table={activeEntry.table} />
                        )}
                        {/* <div className="brokerage-ui">
                          <div className="panel-pager border-top px-4 py-3 d-flex align-items-center justify-content-between">
                            <span>
                              Page {Math.min((teammemberTable.getState().pagination.pageIndex ?? 0) + 1,
                                teammemberTable.getPageCount?.() ?? 1)} of {teammemberTable.getPageCount?.() ?? 1}
                            </span>

                            <div className="d-flex align-items-center gap-3 flex-wrap">
                              <div className="d-flex align-items-center gap-2">
                                <span>Rows per page:</span>
                                <select
                                  className="form-select pageSizeSelect1"
                                  value={teammemberTable.getState().pagination.pageSize}
                                  onChange={(e) => {
                                    teammemberTable.setPageSize(Number(e.target.value));
                                    teammemberTable.setPageIndex(0);
                                  }}
                                >
                                  {[5, 10, 20, 50, 100].map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                              </div>

                              <div className="pager-controls">
                                <button className="pager-chevron"
                                  onClick={() => teammemberTable.previousPage()}
                                  disabled={!teammemberTable.getCanPreviousPage?.()}>
                                  <i className="bi bi-chevron-left" />
                                </button>

                                <button className="pager-chip active">
                                  {(teammemberTable.getState().pagination.pageIndex ?? 0) + 1}
                                </button>

                                <button className="pager-chevron"
                                  onClick={() => teammemberTable.nextPage()}
                                  disabled={!teammemberTable.getCanNextPage?.()}>
                                  <i className="bi bi-chevron-right" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div> */}
                      </>
                    ) : (
                      <div className="px-4 py-4 text-center text-muted" style={{ fontSize: "1.05rem" }}>
                        No team members found
                      </div>
                    )}
                  </div>

                  {/* MOBILE cards only (≤1021px) */}
                  <div className="rdt-mobile">
                    {teammemberLoading ? (
                      <MobileCardsLoader message="Loading team members…" />
                    ) : (
                      <>
                        <MobileCardsOnly table={teammemberTable} idColId="order_link" titleColId="product_name" actionColId="actions" />
                        {activeEntry && (
                          <PaginationControlsView table={activeEntry.table} />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ============== OFFICES (same structure) ============== */}
          <div className={`tab-pane ${activeTab === "#offices" ? "active" : ""}`} id="offices" role="tabpanel">
            <div className="">
              <div >
                <div className="panel shadow-border-datatables box-shadow-datatables m-4">
                  <div className="panel-head px-4 py-3 border-bottom">
                    <h5 className="m-0 view-cards">Office Management</h5>
                  </div>
                  <div className="panel-toolbar px-4 py-3 border-bottom d-flex align-items-center justify-content-between gap-3 flex-wrap">
                    <div className="input-group tm-search">
                      <span className="input-group-text"><i className="bi bi-search" /></span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by Name and City"
                        value={officeSearchInput}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setOfficeSearchInput(e.target.value)}
                        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                          if (e.key === "Enter") {
                            setOfficeGlobalFilter(officeSearchInput);
                          }
                        }}
                      />
                    </div>
                    <Link to={`${baseRoute}/brokerageOffice/${firmId}`} className="btn btn-primary table-addbutton">
                      <i className="bi bi-plus-lg me-2" /> Add Office
                    </Link>
                  </div>

                  <div className="rdt-desktop">
                    {officeLoading ? (
                      <div className="panel-table">
                        <div className="table-wrap " style={{ overflowX: "auto" }}>
                          <table className="table production-table align-middle mb-0 table-1">
                            <DesktopTableLoader table={officeTable} message="Loading offices…" />
                          </table>
                        </div>
                      </div>
                    ) : officeTable?.getRowModel().rows.length ? (
                      <>
                        <div className="panel-table">
                          <div className="table-wrap " style={{ overflowX: "auto" }}>
                            <DataTable table={officeTable} />
                          </div>
                        </div>

                        {activeEntry && (
                          <PaginationControlsView table={activeEntry.table} />
                        )}
                      </>
                    ) : (
                      <div className="px-4 py-4 text-center text-muted" style={{ fontSize: "1.05rem" }}>No records found</div>
                    )}
                  </div>

                  <div className="rdt-mobile">
                    {officeLoading ? (
                      <MobileCardsLoader message="Loading offices…" />
                    ) : (
                      <>
                        <MobileCardsOnly table={officeTable} idColId="id" titleColId="office_name" actionColId="actions" />
                        {activeEntry && (
                          <PaginationControlsView table={activeEntry.table} />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ============== OFFICE INVENTORY ============== */}
          <div className={`tab-pane ${activeTab === "#officeInventory" ? "active" : ""}`} id="officeInventory" role="tabpanel">
            <div className="">
              <div >
                <div className="panel shadow-border-datatables box-shadow-datatables m-4">
                  <div className="panel-head px-4 py-3 border-bottom">
                    <h5 className="m-0 view-cards">Office Inventory</h5>
                  </div>
                  <div className="panel-toolbar px-4 py-3 border-bottom d-flex align-items-center justify-content-between gap-3 flex-wrap">
                    <div className="input-group tm-search">
                      <span className="input-group-text"><i className="bi bi-search" /></span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by Brokerage, Office, Name, Email, Product or SKU"
                        value={officeInventorySearchInput}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setOfficeInventorySearchInput(e.target.value)}
                        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                          if (e.key === "Enter") {
                            setOfficeInventoryGlobalFilter(officeInventorySearchInput);
                          }
                        }}
                      />
                    </div>
                    <Link to={`${baseRoute}/addOfficeProduct/brokerageOffice/${firmId}`} className="btn btn-primary table-addbutton  d-inline-flex align-items-center">
                      <i className="bi bi-plus-lg me-2" /> Add Office Product
                    </Link>
                  </div>

                  <div className="rdt-desktop">
                    {officeInventoryLoading ? (
                      <div className="panel-table">
                        <div className="table-wrap " style={{ overflowX: "auto" }}>
                          <table className="table production-table align-middle mb-0 table-1">
                            <DesktopTableLoader table={officeInventoryTable} message="Loading office inventory…" />
                          </table>
                        </div>
                      </div>
                    ) : officeInventoryTable?.getRowModel().rows.length ? (
                      <>
                        <div className="panel-table">
                          <div className="table-wrap " style={{ overflowX: "auto" }}>
                            <DataTable table={officeInventoryTable} />
                          </div>
                        </div>

                        {activeEntry && (
                          <PaginationControlsView table={activeEntry.table} />
                        )}
                      </>
                    ) : (
                      <div className="px-4 py-4 text-center text-muted" style={{ fontSize: "1.05rem" }}>No records found</div>
                    )}
                  </div>

                  <div className="rdt-mobile">
                    {officeInventoryLoading ? (
                      <MobileCardsLoader message="Loading office inventory…" />
                    ) : (
                      <>
                        <MobileCardsOnly table={officeInventoryTable} idColId="id" titleColId="product_name" actionColId="actions" />
                        {activeEntry && (
                          <PaginationControlsView table={activeEntry.table} />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ============== AGENTS ============== */}
          <div className={`tab-pane ${activeTab === "#agents" ? "active" : ""}`} id="agents" role="tabpanel">
            <div className="">
              <div >
                <div className="panel shadow-border-datatables box-shadow-datatables m-4">
                  <div className="panel-head px-4 py-3 border-bottom">
                    <h5 className="m-0 view-cards">Agent Management</h5>
                  </div>
                  <div className="panel-toolbar px-4 py-3 border-bottom d-flex align-items-center justify-content-between gap-3 flex-wrap">
                    <div className="input-group tm-search">
                      <span className="input-group-text"><i className="bi bi-search" /></span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by Name, Email, License number"
                        value={agentSearchInput}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setAgentSearchInput(e.target.value)}
                        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                          if (e.key === "Enter") {
                            setAgentGlobalFilter(agentSearchInput)
                          }
                        }}
                      />
                    </div>
                    <Link to={`${baseRoute}/brokerageAgent/${firmId}`} state={{ firmId: firmId }} className="btn btn-primary table-addbutton">
                      <i className="bi bi-plus-lg me-2" /> Add Agent
                    </Link>
                  </div>

                  <div className="rdt-desktop">
                    {agentLoading ? (
                      <div className="panel-table">
                        <div className="table-wrap " style={{ overflowX: "auto" }}>
                          <table className="table production-table align-middle mb-0 table-1">
                            <DesktopTableLoader table={agentTable} message="Loading agents…" />
                          </table>
                        </div>
                      </div>
                    ) : agentTable?.getRowModel().rows.length ? (
                      <>
                        <div className="panel-table">
                          <div className="table-wrap " style={{ overflowX: "auto" }}>
                            <DataTable table={agentTable} />
                          </div>
                        </div>

                        {activeEntry && (
                          <PaginationControlsView table={activeEntry.table} />
                        )}

                      </>
                    ) : (
                      <div className="px-4 py-4 text-center text-muted" style={{ fontSize: "1.05rem" }}>No records found</div>
                    )}
                  </div>

                  <div className="rdt-mobile">
                    {agentLoading ? (
                      <MobileCardsLoader message="Loading agents…" />
                    ) : (
                      <>
                        <MobileCardsOnly table={agentTable} idColId="id" titleColId="agent_name" actionColId="actions" />
                        {activeEntry && (
                          <PaginationControlsView table={activeEntry.table} />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ============== AGENT INVENTORY ============== */}
          <div className={`tab-pane ${activeTab === "#agentInventory" ? "active" : ""}`} id="agentInventory" role="tabpanel">
            <div className="">
              <div >
                <div className="panel shadow-border-datatables box-shadow-datatables m-4">
                  <div className="panel-head px-4 py-3 border-bottom">
                    <h5 className="m-0 view-cards">Agent Inventory</h5>
                  </div>
                  <div className="panel-toolbar px-4 py-3 border-bottom d-flex align-items-center justify-content-between gap-3 flex-wrap">
                    <div className="input-group tm-search">
                      <span className="input-group-text"><i className="bi bi-search" /></span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by Agent, Product or SKU"
                        value={agentInventorySearchInput}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setAgentInventorySearchInput(e.target.value)}
                        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                          if (e.key === "Enter") {
                            setAgentInventoryGlobalFilter(agentInventorySearchInput)
                          }
                        }}
                      />
                    </div>
                    <Link to={`${baseRoute}/addAgentProduct/brokerageAgent/${firmId}`} className="btn btn-primary table-addbutton  d-inline-flex align-items-center">
                      <i className="bi bi-plus-lg me-2" /> Add Agent Product
                    </Link>
                  </div>

                  <div className="rdt-desktop">
                    {agentInventoryLoading ? (
                      <div className="panel-table">
                        <div className="table-wrap " style={{ overflowX: "auto" }}>
                          <table className="table production-table align-middle mb-0 table-1">
                            <DesktopTableLoader table={agentInventoryTable} message="Loading agent inventory…" />
                          </table>
                        </div>
                      </div>
                    ) : agentInventoryTable?.getRowModel().rows.length ? (
                      <>
                        <div className="panel-table">
                          <div className="table-wrap " style={{ overflowX: "auto" }}>
                            <DataTable table={agentInventoryTable} />
                          </div>
                        </div>
                        {activeEntry && (
                          <PaginationControlsView table={activeEntry.table} />
                        )}
                      </>
                    ) : (
                      <div className="px-4 py-4 text-center text-muted" style={{ fontSize: "1.05rem" }}>No records found</div>
                    )}
                  </div>

                  <div className="rdt-mobile">
                    {agentInventoryLoading ? (
                      <MobileCardsLoader message="Loading agent inventory…" />
                    ) : (
                      <>
                        <MobileCardsOnly table={agentInventoryTable} idColId="id" titleColId="product_name" actionColId="actions" />
                        {activeEntry && (
                          <PaginationControlsView table={activeEntry.table} />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ============== ADDRESS ============== */}
          <div className={`tab-pane ${activeTab === "#address" ? "active" : ""}`} id="address" role="tabpanel">
            <div className="">
              <div >
                <div className="panel shadow-border-datatables box-shadow-datatables m-4">
                  <div className="panel-head px-4 py-3 border-bottom">
                    <h5 className="m-0 view-cards">Address Management</h5>
                  </div>
                  <div className="panel-toolbar px-4 py-3 border-bottom d-flex align-items-center justify-content-between gap-3 flex-wrap">
                    <div className="input-group tm-search">
                      <span className="input-group-text"><i className="bi bi-search" /></span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by Name and..."
                        value={addressSearchInput}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setAddressSearchInput(e.target.value)}
                        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                          if (e.key === "Enter") {
                            setAddressGlobalFilter(addressSearchInput);
                          }
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary table-addbutton d-inline-flex align-items-center"
                      onClick={() => {
                        setEditingAddress(null);
                        setShowAddressModal(true);
                      }}
                    >
                      <i className="bi bi-plus-lg me-2" /> Add Address
                    </button>
                  </div>

                  <div className="rdt-desktop">
                    {addressLoading ? (
                      <div className="panel-table">
                        <div className="table-wrap " style={{ overflowX: "auto" }}>
                          <table className="table production-table align-middle mb-0 table-1">
                            <DesktopTableLoader table={addressTable} message="Loading addresses…" />
                          </table>
                        </div>
                      </div>
                    ) : addressTable?.getRowModel().rows.length ? (
                      <>
                        <div className="panel-table">
                          <div className="table-wrap " style={{ overflowX: "auto" }}>
                            <DataTable table={addressTable} />
                          </div>
                        </div>
                        {activeEntry && (
                          <PaginationControlsView table={activeEntry.table} />
                        )}
                      </>
                    ) : (
                      <div className="px-4 py-4 text-center text-muted" style={{ fontSize: "1.05rem" }}>No records found</div>
                    )}
                  </div>

                  <div className="rdt-mobile">
                    {addressLoading ? (
                      <MobileCardsLoader message="Loading addresses…" />
                    ) : (
                      <>
                        <MobileCardsOnly table={addressTable} idColId="id" titleColId="label" actionColId="actions" />
                        {activeEntry && (
                          <PaginationControlsView table={activeEntry.table} />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* styles (exact layout) */}
        <style>{`
      /* Tabs */
      .nav-tabs-soft { border-bottom:1px solid #e9eef5; gap:8px; overflow-x:auto; }
      .nav-link-soft { position:relative; border:0!important; background:transparent!important; color:#6b7280; padding:10px 14px; border-radius:8px; font-weight:500; display:inline-flex; align-items:center; white-space:nowrap; }
      .nav-link-soft:hover { color:#111827; }
      .nav-link-soft.active { color:#2563eb; }
      .nav-link-soft.active::after { content:""; position:absolute; left:10px; right:10px; bottom:-1px; height:3px; background:#2563eb; border-radius:3px; }

      /* Inner panel */
      .panel { border:1px solid #edf1f7; background:#fff; overflow:hidden; }
      .panel-head h5 { line-height:1.3; }
      .tm-search { width: clamp(240px, 28vw, 360px); }
      .tm-search .input-group-text { background:#fff; border-right:0; }
      .tm-search .form-control { border-left:0; border-color:#dfe5ef; height:40px; }
      .tm-search .input-group-text, .tm-search .form-control { border-radius:8px; }

      /* Table shell: sticky header + scrollbars (both axis) */
      // .panel-table .table-wrap { max-height: 500px; overflow: auto; }
      .panel-table .table-wrap table { margin-bottom:0; }
      .panel-table thead th { position: sticky; top: 0; z-index: 2; background: #f8fafc; }

      .panel-table .table-wrap::-webkit-scrollbar { height:10px; width:10px; }
      .panel-table .table-wrap::-webkit-scrollbar-thumb { background:#cfd7e6; border-radius:8px; }
      .panel-table .table-wrap::-webkit-scrollbar-track { background:#f3f6fb; }

      // /* Pager (inside panel) */
      // .pager-controls { display:flex; align-items:center; gap:10px; }
      // .pager-chevron { width:38px; height:38px; border-radius:8px; border:1px solid #e5e7eb; background:#fff; display:flex; align-items:center; justify-content:center; }
      // .pager-chevron:disabled { opacity:.5; cursor:not-allowed; }
      // .pager-chip { min-width:38px; height:38px; border-radius:8px; border:1px solid #e5e7eb; background:#fff; display:inline-flex; align-items:center; justify-content:center; font-weight:600; }
      // .pager-chip.active { background:#2563eb; color:#fff; border-color:#2563eb; }
      // .pageSizeSelect { width:92px; height:38px; }

      /* Desktop vs mobile */
      .rdt-desktop { display:block; }
      .rdt-mobile  { display:none; }
      @media (max-width: 1021px) {
        .rdt-desktop { display:none!important; }
        .rdt-mobile  { display:block!important; }
      }

      // .card.shadow-sm, .shadow-sm { box-shadow:0 4px 14px rgba(20,29,56,.06) !important; }
    `}</style>
      </div>


      <DeleteConfirmModal
        open={!!deleteTarget}
        dynamicName={deleteTarget?.name || "Item"}
        onCancel={handleDeleteCancel}
        onDelete={handleDeleteConfirm}
      />

      {/* Address Modal */}
      {showAddressModal && <AddressModal
        isOpen={showAddressModal}
        onClose={() => {
          setShowAddressModal(false);
          setEditingAddress(null);
        }}
        firmId={firmId || ""}
        token={token || ""}
        offices={offices}
        agents={agents}
        editAddress={editingAddress}
        onSuccess={() => {
          setShowAddressModal(false);
          setEditingAddress(null);
          // Refresh addresses - the useEffect will refetch when dependencies change
          // Force a refetch by updating pagination
          setAddressPagination(prev => ({ ...prev }));
        }}
      />}
    </div>
  );
};

// Address Modal Component
type AddressModalProps = {
  isOpen: boolean;
  onClose: () => void;
  firmId: string;
  token: string;
  offices: Office[];
  agents: Agent[];
  editAddress?: Addresses | null;
  onSuccess: () => void;
};

const getAddressFormValues = (
  addr?: Addresses | null,
  offices: Office[] = [],
  agents: Agent[] = []
) => {
  const details = addr?.address_details || (addr as any)?.addressDetails || {};
  const owner = getAddressOwnerSelections(addr);
  const selectedOffices = owner.selectedOffices.map((id) => {
    const match = offices.find(
      (o) => String(o._id) === id || String((o as { id?: string }).id) === id
    );
    return match ? String(match._id || (match as { id?: string }).id) : id;
  });
  const selectedAgents = owner.selectedAgents.map((id) => {
    const match = agents.find(
      (a) => String(a.id) === id || String(a._id) === id
    );
    return match ? String(match.id || match._id) : id;
  });
  return {
    label: addr?.label || "",
    selectedOffices,
    selectedAgents,
    addressLine1: details.addressLine1 || "",
    addressLine2: details.addressLine2 || "",
    city: details.city || "",
    state: details.state || "",
    zipcode: details.zip || "",
    country: details.country || US_COUNTRY_DEFAULTS.country,
    cityCode: details.cityCode || "",
    stateCode: details.stateCode || "",
    countryCode: details.countryCode || US_COUNTRY_DEFAULTS.countryCode,
    latitude: Number(details.latitude ?? 0) || 0,
    longitude: Number(details.longitude ?? 0) || 0,
    phone: details.phone?.toString().trim() || DEFAULT_ADDRESS_PHONE,
  };
};

const uniqueAddressOwnerIds = (...values: unknown[]): string[] => {
  const out: string[] = [];
  const push = (raw: unknown) => {
    const id = String(raw ?? "").trim();
    if (!id || id === "000000000000000000000000" || out.includes(id)) return;
    out.push(id);
  };
  values.forEach((value) => {
    if (Array.isArray(value)) value.forEach(push);
    else push(value);
  });
  return out;
};

const getAddressOwnerSelections = (addr?: Addresses | null) => {
  if (!addr) return { selectedOffices: [] as string[], selectedAgents: [] as string[] };
  const row = addr as Addresses & Record<string, unknown>;
  const type = String(row.user_type ?? row.type ?? "").toUpperCase();
  const userId = String(row.user_id ?? "").trim();
  const officeId = String(row.office_id ?? "").trim();
  const membershipId = String(row.membership_id ?? "").trim();

  let selectedOffices = uniqueAddressOwnerIds(row.office_ids, officeId);
  let selectedAgents = uniqueAddressOwnerIds(row.agent_ids, row.membership_ids, membershipId);

  if (type === "OFFICE") {
    selectedOffices = uniqueAddressOwnerIds(selectedOffices, officeId || userId);
  } else if (type.includes("AGENT")) {
    selectedAgents = uniqueAddressOwnerIds(selectedAgents, membershipId || userId);
  }

  return { selectedOffices, selectedAgents };
};

const resolveEditedAddressOwner = (
  values: { selectedOffices: string[]; selectedAgents: string[] },
  editAddress: Addresses | null | undefined,
  offices: Office[],
  agents: Agent[]
) => {
  const selectedOffices = uniqueAddressOwnerIds(values.selectedOffices);
  const selectedAgents = uniqueAddressOwnerIds(values.selectedAgents);
  const original = getAddressOwnerSelections(editAddress);
  const officesChanged = selectedOffices.join(",") !== original.selectedOffices.join(",");
  const agentsChanged = selectedAgents.join(",") !== original.selectedAgents.join(",");

  let userId = String(editAddress?.user_id || "").trim();
  let userType = String(editAddress?.user_type || "").trim();
  let firstName = editAddress?.first_name || "";
  let lastName = editAddress?.last_name || "";

  const applyOffice = (officeId: string) => {
    const office = offices.find(
      (o) => String(o._id) === officeId || String((o as { id?: string }).id) === officeId
    );
    userId = officeId;
    userType = "OFFICE";
    firstName = office?.officeName || firstName;
    lastName = "";
  };

  const applyAgent = (agentId: string) => {
    const agent = agents.find(
      (a) => String(a.id) === agentId || String(a._id) === agentId
    );
    userId = agentId;
    userType = "BROKERAGE_AGENT";
    firstName = agent?.firstName || firstName;
    lastName = agent?.lastName || lastName;
  };

  if (selectedOffices.length > 0 && selectedAgents.length === 0) {
    applyOffice(selectedOffices[0]);
  } else if (selectedAgents.length > 0 && selectedOffices.length === 0) {
    applyAgent(selectedAgents[0]);
  } else if (selectedOffices.length > 0 && selectedAgents.length > 0) {
    if (officesChanged && !agentsChanged) applyOffice(selectedOffices[0]);
    else if (agentsChanged && !officesChanged) applyAgent(selectedAgents[0]);
    else if (userType.toUpperCase() === "OFFICE") applyOffice(selectedOffices[0]);
    else applyAgent(selectedAgents[0]);
  }

  return {
    user_id: userId,
    user_type: userType,
    first_name: firstName,
    last_name: lastName,
    office_ids: selectedOffices,
    agent_ids: selectedAgents,
  };
};

const AddressModal: React.FC<AddressModalProps> = ({
  isOpen,
  onClose,
  firmId,
  token,
  offices,
  agents,
  editAddress,
  onSuccess,
}) => {
  const isEdit = Boolean(editAddress?._id);
  const [submitting, setSubmitting] = useState(false);
  const toCoordinate = (value: unknown) => {
    const coordinate = Number(value ?? 0);
    return Number.isFinite(coordinate) ? coordinate : 0;
  };

  const officeOptions = useMemo(() => {
    const opts = offices.map((o) => ({
      value: String(o._id || (o as { id?: string }).id || ""),
      label: o.officeName,
    })).filter((o) => o.value);
    const selected = getAddressOwnerSelections(editAddress).selectedOffices;
    selected.forEach((id) => {
      if (opts.some((o) => o.value === id)) return;
      const officeName =
        offices.find((o) => String(o._id) === id || String((o as { id?: string }).id) === id)?.officeName ||
        String(editAddress?.first_name || "").trim() ||
        "Office";
      opts.push({ value: id, label: officeName });
    });
    return opts;
  }, [offices, editAddress]);

  const agentOptions = useMemo(() => {
    const opts = agents.map((a) => ({
      value: String(a.id || a._id || ""),
      label: `${a.firstName || ""} ${a.lastName || ""}`.trim() || "Agent",
    })).filter((o) => o.value);
    const selected = getAddressOwnerSelections(editAddress).selectedAgents;
    selected.forEach((id) => {
      const matched = agents.find(
        (a) => String(a.id) === id || String(a._id) === id
      );
      const optionValue = matched ? String(matched.id || matched._id) : id;
      if (opts.some((o) => o.value === optionValue)) return;
      const agentName = matched
        ? `${matched.firstName || ""} ${matched.lastName || ""}`.trim()
        : `${editAddress?.first_name || ""} ${editAddress?.last_name || ""}`.trim();
      opts.push({ value: optionValue, label: agentName || "Agent" });
    });
    return opts;
  }, [agents, editAddress]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getAddressFormValues(editAddress, offices, agents),
    validationSchema: Yup.object({
      label: Yup.string().required("Label is required"),
      selectedOffices: isEdit
        ? Yup.array()
        : Yup.array().test(
          "office-or-agent",
          "Please select at least one office or agent",
          function (value) {
            const offices = value || [];
            const agents = this.parent.selectedAgents || [];
            return offices.length > 0 || agents.length > 0;
          }
        ),
      selectedAgents: isEdit
        ? Yup.array()
        : Yup.array().test(
          "office-or-agent",
          "Please select at least one office or agent",
          function (value) {
            const agents = value || [];
            const offices = this.parent.selectedOffices || [];
            return offices.length > 0 || agents.length > 0;
          }
        ),
      addressLine1: addressLine1Validation,
      city: cityValidation,
      state: stateValidation,
      zipcode: zipcodeValidation,
      country: Yup.string().required("Country is required"),
    }),
    onSubmit: async (values) => {
      try {
        setSubmitting(true);
        const addressCountry = values.country?.trim() || US_COUNTRY_DEFAULTS.country;
        const addressCountryCode = values.countryCode?.trim() || US_COUNTRY_DEFAULTS.countryCode;
        const addressLatitude = toCoordinate(values.latitude);
        const addressLongitude = toCoordinate(values.longitude);

        if (isEdit && editAddress?._id) {
          const owner = resolveEditedAddressOwner(
            values,
            editAddress,
            offices,
            agents
          );
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/addresses/${editAddress._id}`,
            {
              method: "PUT",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                label: values.label,
                first_name: owner.first_name,
                last_name: owner.last_name,
                company: editAddress.company || "",
                ...(owner.user_id ? { user_id: owner.user_id } : {}),
                ...(owner.user_type ? { user_type: owner.user_type } : {}),
                office_ids: owner.office_ids,
                agent_ids: owner.agent_ids,
                address_details: {
                  label: values.label,
                  addressLine1: values.addressLine1,
                  addressLine2: values.addressLine2 || "",
                  city: values.city,
                  state: values.state,
                  zip: values.zipcode,
                  country: addressCountry,
                  phone: resolveAddressPhone(values.phone),
                  cityCode: values.cityCode,
                  stateCode: values.stateCode,
                  countryCode: addressCountryCode,
                  latitude: addressLatitude,
                  longitude: addressLongitude,
                },
              }),
            }
          );

          if (!response.ok) {
            throw new Error(ADDRESSADDEDIT_MESSAGES.update.error);
          }

          messageHandler.address.success("update");
          formik.resetForm();
          onSuccess();
          return;
        }

        // Prepare bulk address creation payload
        const payload = {
          agent_ids: values.selectedAgents || [],
          office_ids: values.selectedOffices || [],

          address: {
            label: values.label,
            first_name: "",
            last_name: "",
            company: "",
            address_details: {
              addressLine1: values.addressLine1,
              addressLine2: values.addressLine2 || "",
              city: values.city,
              state: values.state,
              zip: values.zipcode,
              country: addressCountry,
              phone: resolveAddressPhone(values.phone),
              cityCode: values.cityCode,
              stateCode: values.stateCode,
              countryCode: addressCountryCode,
              latitude: addressLatitude,
              longitude: addressLongitude,
            },
          },
        };

        // Create addresses in bulk
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/addresses/bulk`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          }
        );

        if (!response.ok) {
          throw new Error(ADDRESSADDEDIT_MESSAGES.create.error);
        }

        const result = await response.json();
        messageHandler.address.success("create");
        formik.resetForm();
        onSuccess();
      } catch (e: any) {
        messageHandler.address.error("create", e);
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    if (!formik.values.phone?.trim()) {
      formik.setFieldValue("phone", DEFAULT_ADDRESS_PHONE, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editAddress?._id]);

  useEffect(() => {
    if (isEdit) return;
    const hasSelection =
      formik.values.selectedOffices.length > 0 ||
      formik.values.selectedAgents.length > 0;
    if (!hasSelection) return;
    if (formik.errors.selectedOffices || formik.errors.selectedAgents) {
      formik.setFieldError("selectedOffices", undefined);
      formik.setFieldError("selectedAgents", undefined);
    }
  }, [formik.values.selectedOffices, formik.values.selectedAgents, isEdit]);

  const hasGoogleCoordinates =
    Number(formik.values.latitude || 0) !== 0 &&
    Number(formik.values.longitude || 0) !== 0;

  const clearAddressDetailErrors = () => {
    (["city", "state", "zipcode", "country"] as const).forEach((field) => {
      formik.setFieldTouched(field, false, false);
      formik.setFieldError(field, undefined);
    });
  };

  const clearGoogleAddressFields = () => {
    formik.setFieldValue("city", "");
    formik.setFieldValue("cityCode", "");
    formik.setFieldValue("state", "");
    formik.setFieldValue("stateCode", "");
    formik.setFieldValue("zipcode", "");
    formik.setFieldValue("country", US_COUNTRY_DEFAULTS.country);
    formik.setFieldValue("countryCode", US_COUNTRY_DEFAULTS.countryCode);
    formik.setFieldValue("latitude", 0);
    formik.setFieldValue("longitude", 0);
  };

  const ensureManualCountryDefaults = () => {
    if (hasGoogleCoordinates || formik.values.country?.trim()) return;
    formik.setFieldValue("country", US_COUNTRY_DEFAULTS.country, false);
    formik.setFieldValue("countryCode", US_COUNTRY_DEFAULTS.countryCode, false);
    formik.setFieldError("country", undefined);
  };

  const handleAddressLine1Change = (value: string) => {
    formik.setFieldValue("addressLine1", value);
    if (hasGoogleCoordinates || !value.trim()) {
      clearGoogleAddressFields();
      clearAddressDetailErrors();
    } else {
      ensureManualCountryDefaults();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", overflowY: "auto" }} onClick={onClose}>
      <div className="modal-dialog modal-dialog-centered address-modal-dialog" onClick={(e) => e.stopPropagation()} style={{ width: "708px", maxWidth: "90vw", margin: "1.75rem auto" }}>
        <div className="modal-content address-modal-content" style={{ borderRadius: 12, maxHeight: "90vh", overflowY: "auto", overflowX: "hidden" }}>
          <div className="modal-header border-0 pb-2 address-modal-header">
            <h5 className="modal-title d-flex align-items-center gap-2">
              <i className="bi bi-house-door text-color" style={{ fontSize: 20 }}></i>
              Location Details
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>
          <hr className="address-modal-hr" />
          <div className="modal-body pt-0 address-modal-body">
            <form onSubmit={formik.handleSubmit}>
              <div className="row g-3">
                <div className="col-md-6">
                  <UIInput
                    formik={formik}
                    name="label"
                    label="Label of address"
                    placeholder="Corporate Office, Affiliate, Branch, Home, etc."
                  />
                </div>
                {isEdit && (
                  <div className="col-md-6">
                    <UIInput
                      formik={formik}
                      name="phone"
                      label="Phone Number"
                      placeholder={DEFAULT_ADDRESS_PHONE}
                    />
                  </div>
                )}
                {!isEdit && (
                  <>
                <div className="col-md-6">
                  <UIReactMultiSelect
                    formik={formik}
                    name="selectedOffices"
                    label="Select Office"
                    // required
                    options={officeOptions}
                    placeholder="Select offices"
                  />
                </div>
                <div className="col-md-6">
                  <UIReactMultiSelect
                    formik={formik}
                    name="selectedAgents"
                    label="Select Agent"
                    // required
                    options={agentOptions}
                    placeholder="Select agents"
                  />
                </div>
                <div className="col-md-6">
                  <UIInput
                    formik={formik}
                    name="phone"
                    label="Phone Number"
                    placeholder={DEFAULT_ADDRESS_PHONE}
                  />
                </div>
                  </>
                )} 
                <div className="col-md-6">
                  <label className="form-label">
                    Address Line 1 <span className="text-danger">*</span>
                  </label>
                  <AddressAutocomplete
                    id="addressLine1"
                    name="addressLine1"
                    value={formik.values.addressLine1}
                    onChange={handleAddressLine1Change}
                    onSelect={(picked) => {
                      formik.setFieldValue("addressLine1", picked.addressLine1);
                      formik.setFieldValue("city", picked.city);
                      formik.setFieldValue("cityCode", picked.cityCode);
                      formik.setFieldValue("state", picked.state);
                      formik.setFieldValue("stateCode", picked.stateCode);
                      formik.setFieldValue("zipcode", picked.zipcode);
                      formik.setFieldValue("country", picked.country);
                      formik.setFieldValue("countryCode", picked.countryCode);
                      formik.setFieldValue("latitude", picked.latitude);
                      formik.setFieldValue("longitude", picked.longitude);
                      formik.setFieldTouched("addressLine1", false, false);
                      formik.setFieldError("addressLine1", undefined);
                      clearAddressDetailErrors();
                    }}
                    className={`form-control br-8 ${formik.touched.addressLine1 && formik.errors.addressLine1 ? "is-invalid" : ""}`}
                    placeholder="Enter a location."
                    error={formik.touched.addressLine1 && formik.errors.addressLine1 ? (formik.errors.addressLine1 as string) : undefined}
                    autoComplete="new-password"
                  />
                </div>
                <div className="col-md-6">
                  <UIInput
                    formik={formik}
                    name="addressLine2"
                    label="Address Line 2"
                    placeholder="Apartment, suite, etc. (optional)"
                  />
                </div>
                <div className="col-md-6">
                  <UIInputDisable
                    formik={formik}
                    name="city"
                    label="City"
                    required
                    placeholder="City"
                    disabled
                    autocomplete="new-password"
                    autoCorrect="off"
                    spellCheck={false}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    State <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="state"
                    className="form-control br-8"
                    placeholder="State"
                    value={formik.values.state}
                    disabled
                    autoComplete="new-password"
                    autoCorrect="off"
                    spellCheck={false}
                  />
                  {formik.touched.state && formik.errors.state && (
                    <div className="invalid-feedback d-block">{formik.errors.state as string}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <UIInputDisable
                    formik={formik}
                    name="zipcode"
                    label="Zip Code"
                    required
                    placeholder="Zip code"
                    disabled
                    autocomplete="new-password"
                    autoCorrect="off"
                    spellCheck={false}
                  />
                </div>
                <div className="col-md-6">
                  <UIInputDisable
                    formik={formik}
                    name="country"
                    label="Country"
                    required
                    placeholder="United States"
                    disabled
                    autocomplete="new-password"
                    autoCorrect="off"
                    spellCheck={false}
                  />
                </div>
              </div>
              <hr />
              <div className="modal-footer border-0 pt-4">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={onClose}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary table-addbutton"
                  disabled={submitting}
                >
                  {submitting ? (isEdit ? "Updating..." : "Adding...") : (isEdit ? "Update Address" : "Add Address")}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <style>{`
        /* Desktop/Tablet - No scrollbar */
        @media (min-width: 769px) {
          .address-modal-dialog {
            width: 708px !important;
          }
          .address-modal-content {
            overflow-y: auto !important;
            overflow-x: hidden !important;
            scrollbar-width: none; /* Firefox */
            -ms-overflow-style: none; /* IE and Edge */
            left:128px
          }
          .address-modal-content::-webkit-scrollbar {
            display: none; /* Chrome, Safari, Opera */
          }
          .address-modal-body {
            overflow-y: visible !important;
            max-height: none !important;
          }
        }
        
        /* Mobile - Show scrollbar, fixed header */
        @media (max-width: 768px) {
          .address-modal-dialog {
            width: 90vw !important;
            margin: 0.5rem auto !important;
            max-height: 100vh;
            left:28px
          }
          .address-modal-content {
            max-height: 95vh !important;
            display: flex !important;
            flex-direction: column !important;
            overflow: hidden !important;
          }
          .address-modal-header {
            position: sticky !important;
            top: 0 !important;
            z-index: 10 !important;
            background: white !important;
            flex-shrink: 0 !important;
            border-bottom: 1px solid #dee2e6 !important;
          }
          .address-modal-hr {
            margin: 0 !important;
            flex-shrink: 0 !important;
          }
          .address-modal-body {
            overflow-y: auto !important;
            overflow-x: hidden !important;
            flex: 1 !important;
            -webkit-overflow-scrolling: touch !important;
            max-height: calc(95vh - 120px) !important;
            padding-bottom: 1rem !important;
          }
          .address-modal-body::-webkit-scrollbar {
            width: 6px !important;
          }
          .address-modal-body::-webkit-scrollbar-track {
            background: #f1f1f1 !important;
            border-radius: 10px !important;
          }
          .address-modal-body::-webkit-scrollbar-thumb {
            background: #888 !important;
            border-radius: 10px !important;
          }
          .address-modal-body::-webkit-scrollbar-thumb:hover {
            background: #555 !important;
          }

        }
          .text-color{
           color:#f6339a;
          }
      `}</style>
    </div>
  );
};

export default SignageBrokeragePage;
